import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import * as XLSX from "xlsx";
import toastr from "../../../utils/toast";
import {
  defaultPersonColumn,
  defaultStatusPick,
  describeSheet,
  distinctStatuses,
  extractRowData,
  readWorkbook,
} from "./excelReader";
import FinishBlocked from "./FinishBlocked";
import CompensationReview, { usableReading } from "./CompensationReview";
// CompensationRecord is no longer rendered - the import worker finds each
// employer and writes each figure now. This was its only caller, so the file
// is currently unreferenced; left on disk rather than deleted until the
// background path has been proved against a real sheet.

/**
 * Import candidates onto a role from a spreadsheet.
 *
 * Only LinkedIn URLs are taken. Everything else about a candidate - name,
 * company, designation, experience - is filled in afterwards by the LinkedIn
 * sync cron, so a row with no LinkedIn URL is skipped rather than half
 * imported. The user can download the skipped rows and chase them by hand.
 *
 * The file is read in the browser and never uploaded. The URLs go to the same
 * endpoint the manual "Add Linkedin Url" form uses, in batches, so this is the
 * existing, proven write path at a larger scale.
 */

// Follows the PINE theme when the template defines it, falls back to the
// colour in the approved design.
const ACCENT = "rgb(var(--primary-rgb, 210, 105, 30))";
const ACCENT_SOFT = "rgba(var(--primary-rgb, 210, 105, 30), 0.08)";
const ACCENT_LINE = "rgba(var(--primary-rgb, 210, 105, 30), 0.28)";

// Every column gets the same width. Real headers vary wildly in length and a
// fixed grid is far easier to scan than a ragged one.
const COL_WIDTH = 170;

const HEADER_CHOICES = 15;

/**
 * A button that shows it is working, in place, instead of blanking the app.
 *
 * Three of the steps here make a model call that runs for 10-30 seconds. They
 * used to raise the OUTLET's global loading flag, which hides the entire page
 * behind one spinner - the screen the user was reading disappears, nothing says
 * which step is running, and a 30-second call looks like a crash.
 *
 * Keeping the screen on and the work inside the button keeps the context the
 * user was just looking at, and the label says what is happening.
 */
const StepButton = ({ busy, busyLabel, children, className, disabled, ...rest }) => (
  <button
    type="button"
    className={className || "btn btn-primary"}
    {...rest}
    // After the spread, not before - `disabled` is pulled out of rest above so
    // this cannot be overwritten. Re-entry is the real hazard: a second click
    // starts a second model call that overwrites the first on arrival.
    disabled={busy || disabled}
  >
    {busy && (
      <span
        className="spinner-border spinner-border-sm me-2"
        role="status"
        aria-hidden="true"
        style={{ verticalAlign: "-2px" }}
      />
    )}
    {busy && busyLabel ? busyLabel : children}
  </button>
);

// People named under each wording on the review screen. Three is enough to
// recognise who a wording covers without the row becoming a list.
const EXAMPLES_PER_WORDING = 3;

// The review screen's four columns. A floor on the wording and the dropdown,
// then both share what is left - a plain percentage table let the wording
// collapse and clip mid-word, and these wordings run to 130 characters.
const MAPPING_GRID = "minmax(250px, 2fr) 56px minmax(230px, 3fr) 112px";

// URLs per request. The endpoint does roughly six queries per URL inside one
// transaction, so a 1,700-URL sheet sent in one go would hold locks for
// minutes. Each batch is its own transaction: if one fails, everything before
// it is already committed.
const CHUNK_SIZE = 100;

// What each column has been given to do, and how that reads in the grid.
// LinkedIn keeps the theme accent because it is the one column the import
// cannot work without; status and notes get their own hues so three roles are
// distinguishable at a glance rather than by shade.
const ROLE = {
  link: {
    label: "LINKEDIN",
    ink: ACCENT,
    soft: ACCENT_SOFT,
    line: ACCENT_LINE,
  },
  status: {
    label: "STATUS",
    ink: "#3f6b46",
    soft: "#eef6ee",
    line: "#bcd6bf",
  },
  notes: {
    label: "NOTES",
    ink: "#3d5f8a",
    soft: "#eef2f8",
    line: "#c3d1e3",
  },
  // Purple, so it is not mistaken for status green or notes blue. Detected
  // from the cells, like the others, but on pay WORDS rather than money shape:
  // an earlier money-shape detector flagged phone numbers, company revenue and
  // LinkedIn URLs, because they share a vocabulary. See
  // detectCompensationColumns in excelReader.js.
  compensation: {
    label: "COMPENSATION",
    ink: "#6b4d8a",
    soft: "#f3eff8",
    line: "#d3c5e3",
  },
};

// Statuses PINE refuses without a résumé, and 48 also without a background
// document. An import has neither, so anyone mapped to one of these is held at
// the default and listed for a human to finish.
//
// The rule lives in editStatus.jsx and NOT in the API - update_candidate_status_data
// accepts anything - so the import has to enforce it itself. Full taxonomy and
// what each status demands: SAKETH-KB/MD/status_map.md.
const NEEDS_A_FILE = new Set([
  27, 28, 29, 30, 31, 32, 33,
  42, 43, 44, 45, 46, 47, 48, 49, 50,
  54,
]);

// Tag colours for the verdict on each sheet: confident, unsure, not candidates.
const TAG_STYLE = {
  good: { color: "#3f6b46", background: "#eef6ee" },
  warn: { color: "#8a5a1b", background: "#fdf3e4" },
  muted: { color: "#8a837c", background: "#f5f2ee" },
};

function ImportFromExcel({ role_id, onClose }) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  // No setGlobalLoading here, deliberately. Every wait in this flow is shown
  // where it was caused - in the button that started it, or on the screen it
  // is filling. The global flag blanks the whole page behind one spinner, and
  // a 30-second model call then looks like a crash.
  const { token } = useOutletContext();

  const [file, setFile] = useState(null);
  const [book, setBook] = useState(null); // null until a file is read
  const [selected, setSelected] = useState([]); // sheet names
  const [active, setActive] = useState(0); // index into book.sheets
  const [reading, setReading] = useState(false);
  const [showHidden, setShowHidden] = useState(false);

  // { [sheetName]: number[] } - which columns to take URLs from.
  const [linkChoice, setLinkChoice] = useState({});

  // Same shape, for the status and notes steps. Seeded once the file is read,
  // so an empty object here means "not chosen yet", not "chosen nothing".
  const [statusChoice, setStatusChoice] = useState({});
  const [notesChoice, setNotesChoice] = useState({});
  // Chosen by hand only. An empty entry means this sheet has no compensation
  // column, which is the normal case.
  const [compChoice, setCompChoice] = useState({});

  // "sheets" -> "columns" -> "mapping" -> "confirm" -> "importing" -> "done"
  const [stage, setStage] = useState("sheets");

  // The review table. { [sheetName]: [{ value, rows, statusId, confidence,
  // note, source, statusName, masterName }] } plus PINE's full status list for
  // the dropdowns, which the mapping endpoint returns alongside.
  const [mapping, setMapping] = useState({});
  const [pineStatuses, setPineStatuses] = useState([]);
  const [mappingBusy, setMappingBusy] = useState(false);
  // { [sheetName]: [{ value, rows, people, currency, fixed, variable, total,
  //   unit, multiplier, isRange, confidence, note, source, ignored }] }
  //
  // Only confirmed figures leave this screen. An untouched row stays ignored,
  // because nobody has looked at it - the same stance the mapping screen takes
  // towards a status it is not sure about.
  const [compReadings, setCompReadings] = useState({});
  const [compBusy, setCompBusy] = useState(false);
  // The last hop before the confirm screen. No model call, but it asks PINE
  // for everyone's current status in batches of 100, so a big sheet leaves
  // Continue dead for a few seconds with nothing to show for it.
  const [confirmBusy, setConfirmBusy] = useState(false);
  // What the compensation panel is doing, as a sentence, or null when it is
  // done. Covers BOTH finding each person's employer and writing the figures:
  // it records on its own now, so leaving the screen at any point before it
  // finishes abandons the rest silently, and there is no button left to press
  // to try again.
  // Held the Close button while compensation was being written in the
  // browser. The worker does that now, so nothing sets it and nothing blocks
  // closing - which is the point. The guards that read it are left in place
  // rather than unpicked from six places in the JSX during a deadline.
  const [compRecording] = useState(null);
  // PINE's currency rows, returned by the parse endpoint. The review screen
  // needs the ids, not the codes - add_compensation takes currency_id.
  const [currencies, setCurrencies] = useState([]);

  /**
   * "Step 3 of 5", worked out rather than written on each screen.
   *
   * The screens used to each state their own total and disagreed - the sheet
   * picker said "Step 2 of 3" while the next one said "Step 3 of 4". Now that
   * two steps are conditional, hardcoding would guarantee it stays wrong.
   *
   * Counted the way the user experiences it: a step that is skipped was never
   * a step.
   */
  const stepsInPlay = () => {
    const steps = ["file", "sheets", "columns"];

    const sheets = book ? chosenSheets() : [];
    if (sheets.some((sheet) => statusIndexes(sheet).length > 0)) {
      steps.push("mapping");
    }
    if (sheets.some((sheet) => compIndexes(sheet).length > 0)) {
      steps.push("compensation");
    }

    return steps;
  };

  const stepLabel = (name) => {
    const steps = stepsInPlay();
    const at = steps.indexOf(name);
    return at === -1 ? "" : `Step ${at + 1} of ${steps.length}`;
  };

  /**
   * One step back, skipping whatever was skipped on the way here.
   *
   * Each screen used to name its own predecessor, which was wrong the moment a
   * step became conditional - Ready-to-import sent you to the sheet picker,
   * past the mapping you had just done. Derived from the same list the step
   * counter uses, so the two cannot disagree.
   *
   * "confirm" is not in that list because it is not a numbered step, so it is
   * handled as the end of it.
   */
  const goBack = (from) => {
    const steps = stepsInPlay();
    const at = from === "confirm" ? steps.length : steps.indexOf(from);
    const previous = at > 0 ? steps[at - 1] : null;

    if (!previous) return;

    if (previous === "file") {
      // There is no "file" stage - the screen shows whenever no book is held.
      setBook(null);
      setFile(null);
      setSelected([]);
      setLinkChoice({});
      setCompChoice({});
      setStarted(false);
      return;
    }

    setStage(previous);
  };
  const [plan, setPlan] = useState(null);

  // What PINE currently says about each person, keyed by URL:
  // { candidate_status_id, deleted }. Fetched once, before the confirm screen.
  //
  // Without it the import cannot tell a correction from a stale row. The
  // browser holds what the sheet maps to; only the server holds what the
  // consultant did, and a disagreement needs both.
  const [currentStatuses, setCurrentStatuses] = useState({});

  // url -> true to use the sheet's status, false to keep PINE's. Seeded when
  // the conflicts are worked out, then owned by the user.
  const [conflictChoice, setConflictChoice] = useState({});

  // Set when the status check could not be run. The import still works - it
  // just behaves as it did before this step existed, and says so rather than
  // showing an empty conflict list that means "we did not look".
  const [statusCheckFailed, setStatusCheckFailed] = useState(false);
  const [sent, setSent] = useState(0);
  // Which of the two calls is running. The progress bar only covers the first,
  // so without this the statuses pass looks like a hang.
  const [phase, setPhase] = useState("urls");

  // A file is being dragged over the drop target. Purely visual - without it
  // there is no way to tell the browser will accept the drop.
  const [dragging, setDragging] = useState(false);
  // The file can be chosen and read freely; what these gate is going on to look
  // at it. The tick is an acknowledgement, not a validation - the detector
  // still reads the cells and the next screen still shows what it found.
  const [formatConfirmed, setFormatConfirmed] = useState(false);
  // Set by Next. Separate from the tick so the screen does not move the instant
  // the box is touched.
  const [started, setStarted] = useState(false);
  // Shown only after Next has been pressed with the box unticked. A warning
  // that is there before anything is done reads as an error the user has
  // already made.
  const [gateWarning, setGateWarning] = useState(false);
  const [result, setResult] = useState(null);

  // .xls is the old binary format and is not handled - saying so on the input
  // is better than a parse failure later.
  const ACCEPT = ".xlsx,.xlsm";

  const sheets = book?.sheets || [];
  const current = sheets[active] || null;

  const linkIndexes = (sheet) =>
    linkChoice[sheet.name] || sheet.linkColumns.map((col) => col.index);

  // Seeded in pickFile, so `[]` here means the user cleared it, not that it
  // was never decided.
  const statusIndexes = (sheet) => statusChoice[sheet.name] || [];
  const notesIndexes = (sheet) => notesChoice[sheet.name] || [];
  const compIndexes = (sheet) => compChoice[sheet.name] || [];

  // What job a column currently has. One column can only hold one, which is
  // what lets the header carry a single control rather than three checkboxes.
  const roleOfColumn = (sheet, index) => {
    if (linkIndexes(sheet).includes(index)) return "link";
    if (statusIndexes(sheet).includes(index)) return "status";
    if (notesIndexes(sheet).includes(index)) return "notes";
    if (compIndexes(sheet).includes(index)) return "compensation";
    return "none";
  };

  /**
   * Give one column a job, taking it away from whatever it had.
   *
   * STATUS takes up to two columns, NOTES any number, LINKEDIN any number.
   *
   * Two status columns exist for the Major/Minor layout, where neither half
   * means much alone: "Dropped by Pmoves" names only the bucket, and "Not
   * Briefed, Not Relevant" names only the reason. Joined, they map onto a PINE
   * status; apart, neither reliably does.
   *
   * Capped at two because every extra column multiplies the combinations - one
   * real sheet went from about 20 distinct wordings to 306 that way, which is
   * more one-off values than a mapping step can be worth. The footer shows the
   * combined count live, so the cost of a second column is visible before
   * Continue is pressed.
   *
   * The detector still offers only ONE by itself - see defaultStatusPick. A
   * user choosing a pair is deliberate; the detector guessing a pair picked
   * "Designation" and "Headquarters" on real sheets.
   */
  const STATUS_COLUMN_LIMIT = 2;

  const setColumnRole = (sheet, index, role) => {
    const name = sheet.name;
    const links = linkIndexes(sheet).filter((i) => i !== index);
    const statuses = statusIndexes(sheet).filter((i) => i !== index);
    const notes = notesIndexes(sheet).filter((i) => i !== index);
    const comps = compIndexes(sheet).filter((i) => i !== index);

    if (role === "status" && statuses.length >= STATUS_COLUMN_LIMIT) {
      toastr.info(
        `A status can come from at most ${STATUS_COLUMN_LIMIT} columns. ` +
          `Set one of the current ones to NOT USED first.`,
      );
      return;
    }

    setLinkChoice({
      ...linkChoice,
      [name]: role === "link" ? links.concat(index) : links,
    });
    setStatusChoice({
      ...statusChoice,
      // Sorted by column, not by click order. extractRowData joins them in
      // array order, and a sheet's major status sits left of its minor one -
      // so "Dropped by Pmoves · Not Briefed, Not Relevant" reads the way the
      // sheet does. Click order would invert it half the time.
      [name]:
        role === "status"
          ? statuses.concat(index).sort((a, b) => a - b)
          : statuses,
    });
    setNotesChoice({
      ...notesChoice,
      [name]: role === "notes" ? notes.concat(index) : notes,
  
    });
    // One per sheet. Unlike links, a second compensation column is not more
    // data about the same person - it is a different figure for them, and
    // nothing here could say which is current.
    setCompChoice({
      ...compChoice,
      [name]: role === "compensation" ? [index] : comps,
    });
  };

  const pickFile = (e) => openFile(e.target.files?.[0] || null);

  /**
   * Reads one file, however it arrived - the picker or a drop.
   *
   * Split out from pickFile so dropping a file and choosing one run exactly the
   * same path, rather than the drop being a second, slightly different reader.
   */
  const openFile = async (chosen) => {
    if (!chosen) return;

    // A drop carries anything the desktop allows. The picker's accept attribute
    // does not apply, so the check has to live here.
    if (!/\.(xlsx|xlsm)$/i.test(chosen.name)) {
      toastr.error(`${chosen.name} is not an .xlsx or .xlsm file.`);
      return;
    }

    setFile(chosen);
    setBook(null);
    setSelected([]);
    setActive(0);
    setShowHidden(false);
    setLinkChoice({});
    setStage("sheets");

    try {
      // The sheets screen has its own "Reading..." state, so blanking the app
      // on top of it only hid the screen that was about to fill in.
      setReading(true);

      const result = await readWorkbook(chosen);
      setBook(result);

      // Nothing is ticked. The verdict tag and the LinkedIn column counts are
      // there to steer the choice, but importing onto a role is a write the
      // user should make deliberately - not one they have to undo.
      setSelected([]);

      // The preview still opens on the most likely sheet, so there is
      // something useful on screen straight away.
      const firstLikely = result.sheets.findIndex(
        (s) => s.level === "good" && s.linkColumns.length > 0,
      );
      setActive(firstLikely >= 0 ? firstLikely : 0);

      // Status, notes and compensation columns ARE pre-ticked, unlike the
      // sheets themselves. Picking a sheet is a decision about what to write;
      // picking a column is a correction to a guess, and detection only ticks
      // a status column when its header actually says so.
      const seedStatus = {};
      const seedNotes = {};
      const seedComp = {};
      result.sheets.forEach((sheet) => {
        seedStatus[sheet.name] = defaultStatusPick(sheet.statusColumns).map(
          (col) => col.index,
        );
        seedNotes[sheet.name] = sheet.notesColumns
          .slice(0, 3)
          .map((col) => col.index);
        // One column only. Pay is one figure per person at one company, so a
        // second column would mean two answers with no way to say which is
        // current. Sheets that split fixed and variable put both in one cell
        // anyway - "INR 1.6 CR fixed + 35 lakh bonus".
        seedComp[sheet.name] = (sheet.compensationColumns || [])
          .slice(0, 1)
          .map((col) => col.index);
      });
      setStatusChoice(seedStatus);
      setNotesChoice(seedNotes);
      setCompChoice(seedComp);

      const anyUsable = result.sheets.some((s) => s.linkColumns.length > 0);
      if (!anyUsable) {
        toastr.warning("No LinkedIn URLs found anywhere in that file.");
      }
    } catch (err) {
      console.error(err);
      toastr.error(err.message || "Could not read that file.");
      setFile(null);
      setBook(null);
    } finally {
      setReading(false);
    }
  };

  const toggleSheet = (name) => {
    setSelected((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : prev.concat(name),
    );
  };

  const toggleLinkColumn = (sheet, index) => {
    const now = linkIndexes(sheet);
    setLinkChoice({
      ...linkChoice,
      [sheet.name]: now.includes(index)
        ? now.filter((i) => i !== index)
        : now.concat(index),
    });
  };

  const allSelected = sheets.length > 0 && selected.length === sheets.length;

  const toggleAll = () => {
    setSelected(allSelected ? [] : sheets.map((s) => s.name));
  };

  // The user corrects the detected header row. Re-describe just that sheet -
  // the workbook is already in memory, so nothing is re-read from disk.
  const changeHeaderRow = (rowIndex) => {
    if (!book || !current) return;

    const redone = describeSheet(
      book.workbook.Sheets[current.name],
      current.name,
      rowIndex,
    );

    // Column positions move with the header row, so any hand-picked link
    // columns no longer mean what they meant. Fall back to detection.
    const { [current.name]: _dropped, ...rest } = linkChoice;
    setLinkChoice(rest);

    setBook({
      ...book,
      sheets: sheets.map((s, i) => (i === active ? redone : s)),
    });
  };

  /* ---------------------------------------------------------------- */
  /* Building and running the import                                   */
  /* ---------------------------------------------------------------- */

  // Sheets the user ticked, in sheet order.
  const chosenSheets = () => sheets.filter((s) => selected.includes(s.name));

  // `urlsOnly` is passed rather than read from state because "Skip statuses"
  // decides and imports in one go: setting state then reading it back in the
  // same handler would read the previous value.
  const buildPlan = (urlsOnly) => {
    const chosen = chosenSheets();

    const perSheet = chosen.map((sheet) => {
      const status = urlsOnly ? [] : statusIndexes(sheet);

      const found = extractRowData(book.workbook.Sheets[sheet.name], sheet.columns, {
        link: linkIndexes(sheet),
        status,
        // No status means no status change, and a person whose status did not
        // move already has bulk_add_linkedin_url's "Added to search map" entry.
        // There is nothing for a note to attach to, so it is not collected.
        notes: status.length === 0 ? [] : notesIndexes(sheet),
        // Collected whatever the status does. Compensation attaches to the
        // person, not to a status change, and is the one thing on this screen
        // that is still useful when the sheet said nothing about where they are
        // in the search.
        compensation: urlsOnly ? [] : compIndexes(sheet),
        // Shown on the review screen and the finished list, never sent.
        person: defaultPersonColumn(sheet.columns),
      });
      return { sheet, ...found, distinct: distinctStatuses(found.rows) };
    });

    // One person can sit on two sheets of the same workbook. Send them once -
    // the first sheet they appear on wins, so the row that carries their
    // status and notes is kept rather than a bare second mention.
    const seen = new Set();
    const rows = [];
    let crossSheetDuplicates = 0;

    perSheet.forEach(({ rows: sheetRows, sheet }) => {
      sheetRows.forEach((row) => {
        if (seen.has(row.url)) {
          crossSheetDuplicates += 1;
          return;
        }
        seen.add(row.url);
        rows.push({ ...row, sheet: sheet.name });
      });
    });

    return {
      rows,
      urls: rows.map((row) => row.url),
      perSheet,
      // Every distinct status across all selected sheets, which is what gets
      // mapped. Counted here so the same wording on two sheets is one entry.
      distinct: distinctStatuses(rows),
      withStatus: rows.filter((row) => row.status).length,
      duplicates:
        perSheet.reduce((sum, s) => sum + s.duplicates, 0) + crossSheetDuplicates,
      skippedCount: perSheet.reduce((sum, s) => sum + s.skipped.length, 0),
    };
  };

  /**
   * Straight past the status step when there is nothing there to decide.
   *
   * A file whose sheets carry no status and no notes columns behaves exactly as
   * it did before this step existed - the screen never appears.
   */
  const goToColumns = () => {
    const anything = chosenSheets().some(
      (sheet) => sheet.statusColumns.length > 0 || sheet.notesColumns.length > 0,
    );

    if (!anything) {
      goToConfirm(true);
      return;
    }

    setStage("columns");
  };

  /**
   * Asks the server to suggest a PINE status for each wording found.
   *
   * One call per sheet rather than one for all of them, because the column
   * header is part of the question: "Not suitable" under a column called
   * "Client Status" is Rejected By Client, and under a researcher's column it
   * is Dropped by PMoves.
   *
   * Distinct wordings only - a 193-row sheet holding 67 of them is one call,
   * not 193.
   */
  const goToMapping = async () => {
    const sheetsWithStatus = chosenSheets().filter(
      (sheet) => statusIndexes(sheet).length > 0,
    );

    if (sheetsWithStatus.length === 0) {
      // No status to map. Compensation may still have been picked, and it is
      // read whether or not statuses are being written - so the links-only
      // decision is carried through rather than ending the flow here.
      goToCompensation(true);
      return;
    }

    // The screen does NOT move yet. Leaving early and showing a spinner where
    // the answers will be puts an empty screen in front of the user and takes
    // away the one they were reading. The wait belongs in the button they
    // pressed; the next screen appears when it has something on it.
    setMappingBusy(true);

    const next = {};
    let statuses = [];

    try {
      for (const sheet of sheetsWithStatus) {
        const found = extractRowData(
          book.workbook.Sheets[sheet.name],
          sheet.columns,
          {
            link: linkIndexes(sheet),
            status: statusIndexes(sheet),
            notes: notesIndexes(sheet),
            compensation: compIndexes(sheet),
            person: defaultPersonColumn(sheet.columns),
          },
        );

        const distinct = distinctStatuses(found.rows);
        if (distinct.length === 0) continue;

        // A few of the people each wording covers, so the review screen can
        // show who is being ruled on. One wording can cover eight people, and
        // judging it in the abstract is guesswork.
        const examples = new Map();
        found.rows.forEach((row) => {
          if (!row.status) return;
          const list = examples.get(row.status) || [];
          if (list.length < EXAMPLES_PER_WORDING) {
            list.push({ person: row.person, number: row.number, url: row.url });
          }
          examples.set(row.status, list);
        });

        const header = statusIndexes(sheet)
          .map((index) => {
            const col = sheet.columns.find((c) => c.index === index);
            return col ? col.label || col.letter : "";
          })
          .filter(Boolean)
          .join(" · ");

        const res = await axios.post(
          `${API_BASE_URL}/candidates/map_status_values`,
          {
            values: distinct.map((d) => d.value),
            column_header: header,
            role_id,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );

        const counts = new Map(distinct.map((d) => [d.value, d.rows]));

        next[sheet.name] = (res.data?.mappings || [])
          .map((m) => ({
            ...m,
            rows: counts.get(m.value) || 0,
            people: examples.get(m.value) || [],
          }))
          // Least certain first: those are the ones worth a human's attention,
          // and the confident ones can be skimmed.
          .sort((a, b) => a.confidence - b.confidence);

        if (res.data?.statuses?.length) statuses = res.data.statuses;
      }

      setMapping(next);
      setPineStatuses(statuses);
      // Only now, with answers to show.
      setStage("mapping");
    } catch (err) {
      console.error(err);
      toastr.error(
        err.response?.data?.error || "Could not map the statuses on that sheet.",
      );
      // Nowhere to send them - the screen never moved. The toast is the whole
      // report, and their column choices are still on screen to try again.
    } finally {
      setMappingBusy(false);
    }
  };

  /**
   * Read the compensation column, if one was picked.
   *
   * Mirrors goToMapping deliberately: distinct wordings rather than rows, a few
   * of the people each covers, least certain first. The model does the reading
   * and the server checks every number against the cell it came from, because a
   * deterministic parser was tried first and was confidently wrong a third of
   * the time - see compensationParser.js.
   *
   * Skipped entirely when no sheet has a compensation column, which is the
   * normal case. `urlsOnly` is carried through rather than decided here: this
   * step has no opinion on whether statuses are being written.
   */
  const goToCompensation = async (urlsOnly) => {
    const sheetsWithComp = chosenSheets().filter(
      (sheet) => compIndexes(sheet).length > 0,
    );

    if (sheetsWithComp.length === 0) {
      goToConfirm(urlsOnly);
      return;
    }

    // Same as goToMapping: the screen waits until there is something on it.
    setCompBusy(true);

    const next = {};

    try {
      for (const sheet of sheetsWithComp) {
        const found = extractRowData(
          book.workbook.Sheets[sheet.name],
          sheet.columns,
          {
            link: linkIndexes(sheet),
            status: statusIndexes(sheet),
            notes: notesIndexes(sheet),
            compensation: compIndexes(sheet),
            person: defaultPersonColumn(sheet.columns),
          },
        );

        const counts = new Map();
        const examples = new Map();

        found.rows.forEach((row) => {
          if (!row.compensation) return;
          counts.set(row.compensation, (counts.get(row.compensation) || 0) + 1);
          const list = examples.get(row.compensation) || [];
          if (list.length < EXAMPLES_PER_WORDING) {
            list.push({ person: row.person, number: row.number, url: row.url });
          }
          examples.set(row.compensation, list);
        });

        if (counts.size === 0) continue;

        const header = compIndexes(sheet)
          .map((index) => {
            const col = sheet.columns.find((c) => c.index === index);
            return col ? col.label || col.letter : "";
          })
          .filter(Boolean)
          .join(" · ");

        const res = await axios.post(
          `${API_BASE_URL}/candidates/parse_compensation_values`,
          { values: [...counts.keys()], column_header: header },
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (res.data?.currencies?.length) setCurrencies(res.data.currencies);

        next[sheet.name] = (res.data?.readings || [])
          .map((reading) => ({
            ...reading,
            rows: counts.get(reading.value) || 0,
            people: examples.get(reading.value) || [],
            // Ticked already when the row needs nothing further - it has a
            // currency and a figure, so it could be written exactly as it
            // stands. Everything else starts off, which is most of what the
            // user has to look at anyway: a missing currency, or no number at
            // all. The screen still sorts least-certain first, and the footer
            // counts how many of the ticked ones the model doubted.
            ignored: !usableReading(reading),
          }))
          .sort((a, b) => a.confidence - b.confidence);
      }

      setCompReadings(next);
      setStage("compensation");
    } catch (err) {
      console.error(err);
      toastr.error(
        err.response?.data?.error ||
          "Could not read the compensation on that sheet.",
      );
      // Stay put. Sending them back to the columns screen used to throw away
      // the status mapping they had just checked.
    } finally {
      setCompBusy(false);
    }
  };

  /**
   * What PINE currently says about the people in this plan.
   *
   * Only asked when statuses are in play. A links-only import cannot disagree
   * with anything, so it makes no request and behaves exactly as it did before
   * this step existed.
   *
   * Chunked like every other call in the flow. The endpoint reads one role's
   * rows, so each chunk costs about 30ms - the scan is not the cost here.
   */
  const fetchCurrentStatuses = async (urls) => {
    const byUrl = {};

    for (let i = 0; i < urls.length; i += CHUNK_SIZE) {
      const batch = urls.slice(i, i + CHUNK_SIZE);

      const res = await axios.post(
        `${API_BASE_URL}/candidates/current_statuses_for_role`,
        { role_id, linkedin_urls: batch },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      (res.data?.statuses || []).forEach((entry) => {
        byUrl[entry.linkedin_url] = entry;
      });
    }

    return byUrl;
  };

  const goToConfirm = async (urlsOnly) => {
    setConfirmBusy(true);
    try {
      const built = buildPlan(urlsOnly);

      if (built.urls.length === 0) {
        toastr.error(
          "No LinkedIn URLs found in the selected sheets. Check the LinkedIn column is ticked.",
        );
        return;
      }

      let statuses = {};
      let failed = false;

      if (!urlsOnly) {
        try {
          statuses = await fetchCurrentStatuses(built.urls);
        } catch (err) {
          console.error(err);
          failed = true;
          toastr.warning(
            "Could not check existing statuses. Anyone already moved off " +
              "Prospective / New will keep the status they have.",
          );
        }
      }

      setCurrentStatuses(statuses);
      setStatusCheckFailed(failed);
      setConflictChoice(defaultConflictChoices(built, statuses));
      setPlan(built);
      setStage("confirm");
    } catch (err) {
      console.error(err);
      toastr.error("Could not read the LinkedIn URLs from that sheet.");
    } finally {
      setConfirmBusy(false);
    }
  };

  /**
   * Rows where the sheet and PINE disagree about someone's status.
   *
   * Derived rather than stored, so editing the mapping and coming back cannot
   * leave a stale list behind.
   *
   * A row only asks a question when every one of these holds. Any one failing
   * means today's behaviour, unchanged:
   *
   *   the sheet gave a status        or there is nothing to apply
   *   the wording mapped to an id    or there is nothing to apply
   *   they are already on the role   or they will be created at 12
   *   they are not deleted           a removal is a refusal, not a disagreement
   *   they are not at 12             or this is the ordinary move
   *   the ids differ                 or nothing would change
   */
  const conflictsFor = (built, statuses) => {
    if (!built) return [];

    const out = [];

    for (const row of built.rows) {
      if (!row.status) continue;

      const wanted = statusIdFor(row.sheet, row.status);
      if (!wanted) continue;

      const current = statuses[row.url];
      if (!current || current.deleted) continue;

      const now = Number(current.candidate_status_id);
      if (now === 12 || now === wanted) continue;

      out.push({ ...row, currentStatusId: now, wantedStatusId: wanted });
    }

    return out;
  };

  /**
   * How each conflict starts out.
   *
   * Use the sheet's, because correcting the sheet is usually why it is being
   * re-imported - the same reasoning as the mapping screen, which pre-fills the
   * likely answer and lets the exceptions be corrected.
   *
   * Except when the sheet maps to 12. Moving someone from Recommended back to
   * New is almost never a correction; it is a row nobody updated. Still
   * offered, not defaulted.
   */
  const defaultConflictChoices = (built, statuses) => {
    const choice = {};

    for (const row of conflictsFor(built, statuses)) {
      choice[row.url] = row.wantedStatusId !== 12;
    }

    return choice;
  };

  /**
   * The PINE status the review screen settled on for one sheet wording.
   *
   * Reads the mapping the user has seen and possibly corrected, never the raw
   * model answer - a correction made on the review screen is the answer.
   */
  const statusIdFor = (sheetName, value) => {
    const forSheet = mapping[sheetName];
    if (!forSheet) return null;

    const hit = forSheet.find((row) => row.value === value);
    return hit && hit.statusId ? hit.statusId : null;
  };

  /**
   * A status id as the user reads it: "Briefed · Interested - CV/resume awaited".
   *
   * Master and minor together, because a minor alone is ambiguous - "On Hold"
   * exists under two masters and "Briefed - not interested" only makes sense
   * underneath Dropped by PMoves.
   *
   * pineStatuses arrives with the mapping response. A links-only import never
   * has it, and never has a conflict to label either.
   */
  const statusLabel = (id) => {
    const hit = pineStatuses.find((row) => row.minor_id === id);
    return hit ? `${hit.master_status} · ${hit.minor_status}` : `status ${id}`;
  };

  /**
   * What gets written into filterlogs.comments for one row.
   *
   * Spec §4: the sheet's own words first, then one labelled line per notes
   * column, then where it came from. The wording is copied verbatim and never
   * rephrased - the mapping is a translation, and translations can be wrong, so
   * the original has to stay readable next to it.
   *
   * Returns "" when the row said nothing, and nothing is written for it.
   */
  const buildComment = (row) => {
    const lines = [];

    if (row.status) lines.push(`Sheet status: "${row.status}"`);
    (row.notes || []).forEach((note) =>
      lines.push(`${note.label}: ${note.value}`),
    );

    if (lines.length === 0) return "";

    lines.push(
      `Imported from ${book.fileName} · sheet ${row.sheet} · row ${row.number}`,
    );
    return lines.join("\n");
  };

  /**
   * One entry per person the sheet had something to say about.
   *
   * Rows the sheet said nothing about are left out entirely rather than sent
   * with an empty comment: they already have bulk_add_linkedin_url's "Added to
   * search map" entry and a second row saying nothing new would be noise.
   */
  /**
   * The call notes, as the consultant wrote them, for the reporting read.
   *
   * NOT buildComment's output. That prefixes the sheet status and appends
   * "Imported from <file> · sheet <name> · row <n>", and the model is asked to
   * copy spans out of what it is given - so our own scaffolding would be
   * quotable and could end up stored as somebody's reporting line.
   *
   * Every column the user tagged NOTES, joined. Which columns those are, and
   * what the writing looks like, differs per file; nothing here inspects the
   * text.
   */
  const buildReportingItems = () =>
    plan.rows
      .map((row) => ({
        linkedin_url: row.url,
        notes: (row.notes || [])
          .map((note) => note.value)
          .filter(Boolean)
          .join("\n\n"),
      }))
      .filter((item) => item.linkedin_url && item.notes);

  const buildSheetItems = () => {
    // Only rows the user was actually shown carry a decision. A row with no
    // conflict has none, and the server keeps its existing rule for those.
    const conflicted = new Set(
      conflictsFor(plan, currentStatuses).map((row) => row.url),
    );

    return plan.rows
      .map((row) => ({
        linkedin_url: row.url,
        candidate_status_id: row.status ? statusIdFor(row.sheet, row.status) : null,
        comment: buildComment(row),
        // Ignored by the server's writes, carried so the job can rebuild the
        // Finish-these screen for whoever it blocks. Those people's name,
        // what the sheet said and the pay wording exist nowhere but the
        // spreadsheet, and the spreadsheet is gone the moment the modal
        // closes. Only the blocked few are kept in `result`; the rest go with
        // `payload` when the job finishes.
        person: row.person || "",
        said: row.status || "",
        compensationSaid: row.compensation || "",
        sheet: row.sheet || "",
        number: row.number || "",
        ...(conflicted.has(row.url)
          ? { overwrite: Boolean(conflictChoice[row.url]) }
          : {}),
      }))
      .filter((item) => item.candidate_status_id || item.comment);
  };

  /**
   * What the confirm screen predicts will happen to the statuses.
   *
   * A prediction, not the answer. The browser cannot know who already has a CV
   * on file, so a row wanting 27, 31 or 32 is counted as needing a human here
   * and may still go through - the server checks candidate_has_files and the
   * summary reports what actually happened.
   */
  const statusForecast = () => {
    let moved = 0;
    let byHand = 0;
    let unmapped = 0;
    let alreadyNew = 0;
    let noStatus = 0;
    let unmappedExample = "";

    plan.rows.forEach((row) => {
      if (!row.status) {
        noStatus += 1;
        return;
      }

      const statusId = statusIdFor(row.sheet, row.status);

      if (!statusId) {
        unmapped += 1;
        if (!unmappedExample) unmappedExample = row.status;
      } else if (NEEDS_A_FILE.has(statusId)) {
        byHand += 1;
      } else if (statusId === 12) {
        // Mapped correctly, and to where they already are. Counted apart from
        // `moved` because writing 12 over 12 is not movement, and calling it
        // movement is what made the old screen misleading.
        alreadyNew += 1;
      } else {
        moved += 1;
      }
    });

    return { moved, byHand, unmapped, alreadyNew, noStatus, unmappedExample };
  };

  /** How often the screen asks the server how the job is getting on. */
  const POLL_MS = 2000;

  /**
   * Polling stops when the modal goes away. The job does not - it is running
   * on the server, which is not listening to this browser. The only thing
   * lost by closing the window is the live count.
   */
  const closed = useRef(false);
  useEffect(
    () => () => {
      closed.current = true;
    },
    [],
  );

  /**
   * An import already running on this role, looked for whenever the modal is
   * open.
   *
   * Without this the only way to find out whether a job had finished was to
   * start another one and be refused - which says it is running and nothing
   * else, and makes the user re-pick the file to learn it.
   */
  const [liveJob, setLiveJob] = useState(null);

  /**
   * How many people this import is for.
   *
   * Held apart from `plan` because the progress screen has to render for a
   * job picked up after the window was closed, when there is no spreadsheet
   * in the browser at all.
   */
  const [total, setTotal] = useState(0);

  /**
   * The import this window is currently showing, if it came from the server.
   *
   * Needed because Done is what ends an import - the phases finishing does
   * not. Clicking it tells the server, and only then does the next visit
   * start at the file picker.
   */
  const [openJobId, setOpenJobId] = useState(null);

  /**
   * True until we know whether this role has an import already under way.
   *
   * The file picker is not rendered while this is true. Showing it first and
   * switching a moment later meant a consultant with an import half finished
   * was asked for the spreadsheet again - the one thing this whole change
   * exists to stop.
   */
  const [checking, setChecking] = useState(true);

  /**
   * Someone else's import on this role.
   *
   * A second import is refused role-wide, so being told at the end - after
   * choosing a sheet, picking columns and correcting fifteen status mappings
   * - is the worst possible moment. This is how the screen says so first.
   */
  const [othersImport, setOthersImport] = useState(null);

  /**
   * PINE's statuses, loaded when the modal opens.
   *
   * These used to arrive only as a by-product of the status mapping call,
   * which runs once, right after a spreadsheet is read. Reopening an
   * unfinished import never reads a spreadsheet, so the list stayed empty -
   * and the Finish-these screen then sent a blank master status, which the
   * database refused. The person stayed at Prospective / New and nothing on
   * screen said why.
   *
   * The mapping call still overwrites this with its own copy; it is the same
   * list.
   */
  useEffect(() => {
    let gone = false;

    axios
      .get(`${API_BASE_URL}/candidates/pine_statuses`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (gone) return;
        const rows = res.data?.statuses || [];
        if (rows.length) setPineStatuses(rows);
      })
      .catch((err) =>
        console.error("[import] status list failed:", err.message),
      );

    return () => {
      gone = true;
    };
  }, [token, API_BASE_URL]);

  /**
   * Done on the finish screen. The one thing that marks an import over.
   *
   * A failure to record it is not worth blocking the user on: the worst case
   * is they are shown this summary again next time, which is the safe
   * direction to fail in.
   */
  const endImport = async () => {
    if (openJobId) {
      try {
        await axios.post(
          `${API_BASE_URL}/candidates/import_job/${openJobId}/close`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (err) {
        console.error("[import] could not close the job:", err.message);
      }
    }
    onClose();
  };

  // What is on screen right now, readable from inside the interval below.
  // The effect runs once, so `stage` in its closure is frozen at "sheets"
  // forever and cannot be used to decide anything.
  const stageRef = useRef(stage);
  stageRef.current = stage;

  useEffect(() => {
    let gone = false;
    const auth = { headers: { Authorization: `Bearer ${token}` } };

    const look = async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/candidates/import_jobs/${role_id}`,
          auth,
        );
        if (gone) return;

        const list = res.data?.jobs || [];
        const live =
          list.find((j) => j.status === "queued" || j.status === "running") ||
          null;

        setLiveJob(live);

        // Anyone's import on this role. `jobs` above is only yours; this is
        // the one that can belong to a colleague, and it is what stops you
        // doing twenty minutes of mapping for a sheet that will be refused.
        const active = res.data?.active || null;
        setOthersImport(active && !active.is_mine ? active : null);

        // Open on the import that is already happening, rather than on the
        // file picker. Only while the file picker is what is showing: once
        // the user is anywhere else, taking over the screen would throw away
        // whatever they were doing.
        if (live) return;

        // Nothing running, but an import the consultant never finished with.
        // `done` means the phases ran; it does not mean they are done - they
        // may be halfway through uploading CVs. Reopen on their summary
        // rather than asking for the spreadsheet again.
        //
        // A job they HAVE finished with reads 'complete' and is skipped, so
        // the next import starts at the file picker as it should.
        // Also from "importing": when the job finishes, this is what moves
        // the screen on to the summary. watchJob is supposed to, but if its
        // poll is failing the screen would otherwise sit on a full progress
        // bar forever.
        if (
          !live &&
          (stageRef.current === "sheets" || stageRef.current === "importing")
        ) {
          const last = list.find((j) => j.status === "done");
          if (!last) return;

          // A missing or unreadable summary is NOT a reason to fall back to
          // the file picker. The import is still unfinished either way, and
          // showing the picker is how a consultant ends up re-uploading a
          // sheet they already imported. Show what there is - even if that
          // is only the counts - and let Done close it.
          let out = {};
          try {
            out =
              (typeof last.result === "string"
                ? JSON.parse(last.result)
                : last.result) || {};
          } catch (err) {
            console.error("[import] job result unreadable:", err.message);
          }

          setOpenJobId(last.id);
          setTotal(last.total || 0);
          setSent(last.done || 0);
          setResult({
            added: out.added || 0,
            failedAt: null,
            error: null,
            record: out.record || null,
            reporting: out.reporting || null,
            compensation: out.compensation || null,
          });
          setStage("done");
        }
      } catch (err) {
        // Logged, not swallowed. A check that fails every time looks exactly
        // like a role with no imports, and that is how this screen spent an
        // afternoon showing a file picker instead of a running job.
        console.error(
          "[import] job list failed:",
          err.response?.status,
          err.response?.data || err.message,
        );
      } finally {
        // Whatever happened, we have asked. A failed check must not hide the
        // picker forever - the consultant still has an import to run.
        if (!gone) setChecking(false);
      }
    };

    look();

    // Often while the progress screen is up, rarely otherwise. A 400-row
    // import deserves a count that moves; a role nobody is importing into
    // does not deserve a request every two seconds for as long as the modal
    // is open.
    const timer = setInterval(
      look,
      stage === "importing" ? POLL_MS : POLL_MS * 2,
    );

    return () => {
      gone = true;
      clearInterval(timer);
    };
    // `stage` is here so the cadence above actually changes when the
    // progress screen comes up. watchJob is left out deliberately - it is
    // stable for the life of the modal, and listing it would restart the
    // interval on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role_id, token, API_BASE_URL, stage]);

  /**
   * An import is running on this role: show it, rather than a file picker
   * that cannot be used. Starting a second import is refused by the server,
   * so a drop zone there is an invitation to be told no.
   *
   * Its own effect rather than a branch inside the fetch callback. That
   * version depended on the fetch landing at the exact moment the screen was
   * the picker, and quietly did nothing when it didn't.
   */
  useEffect(() => {
    if (!liveJob || stage !== "sheets") return;

    setOpenJobId(liveJob.id);
    setTotal(liveJob.total || 0);
    setSent(liveJob.done || 0);
    setPhase(
      liveJob.phase === "sheet" ? "statuses" : liveJob.phase || "urls",
    );
    setStage("importing");

    watchJob(liveJob.id, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // watchJob is stable for the life of the modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveJob, stage, token]);

  /**
   * Keep the progress screen current from the role-level poll.
   *
   * watchJob polls the job directly and is meant to do this, but if that
   * request fails the screen sits frozen while the import runs - which looks
   * identical to a hung import. This runs off the listing, which is the call
   * known to be working, so the count moves either way.
   */
  useEffect(() => {
    if (!liveJob || stage !== "importing") return;

    setSent(liveJob.done || 0);
    setPhase(
      liveJob.phase === "sheet" ? "statuses" : liveJob.phase || "urls",
    );
    setTotal((current) => current || liveJob.total || 0);
  }, [liveJob, stage]);

  /**
   * Hand the whole plan to the server and stop waiting for it.
   *
   * This used to be three calls in a row - URLs, then statuses and notes,
   * then the reporting lines - with the consultant held through all of them,
   * and then held again on the finish screen while each person's employer was
   * fetched at 31 to 58 seconds each. A 400-row sheet with six compensation
   * figures was the better part of ten minutes of watching a spinner.
   *
   * One call now, and the work happens without anyone present.
   */
  const runImport = async () => {
    setStage("importing");
    setSent(0);
    setTotal(plan.urls.length);
    setPhase("urls");

    const auth = { headers: { Authorization: `Bearer ${token}` } };

    try {
      const started = await axios.post(
        `${API_BASE_URL}/candidates/start_import`,
        {
          role_id,
          urls: plan.urls,
          items: buildSheetItems(),
          reportingItems: buildReportingItems(),
          // Currency stays a name and the figures stay unscaled. The server
          // resolves the id and applies the multiplier, so what is stored is
          // a record of what the user confirmed rather than a pre-chewed
          // request body nobody can read back.
          compensation: compensationToRecord().map((entry) => ({
            url: entry.url,
            currency: entry.compensation.currency,
            fixed: entry.compensation.fixed,
            variable: entry.compensation.variable,
            total: entry.compensation.total,
            multiplier: entry.compensation.multiplier,
          })),
        },
        auth,
      );

      const jobId = started.data?.job_id;
      if (!jobId) throw new Error("The server did not return a job id.");

      setOpenJobId(jobId);

      await watchJob(jobId, auth);
    } catch (err) {
      console.error(err);

      // 409 earns its own wording: someone else is already importing into
      // this role, and running a second would interleave two sheets into one
      // search. Everything else is a plain failure to start.
      const conflict = err.response?.status === 409;

      setResult({
        added: 0,
        failedAt: null,
        error: conflict
          ? "An import is already running on this role. Wait for it to finish."
          : err.response?.data?.error ||
            err.response?.data?.message ||
            "The server would not start the import.",
      });
      setStage("done");
    }
  };

  /**
   * Follow a job until it ends.
   *
   * A failed poll is ignored rather than fatal. The job is running on the
   * server either way, and one dropped request is not a failed import - only
   * the job's own status ends this loop.
   */
  const watchJob = async (jobId, auth) => {
    for (;;) {
      await new Promise((resolve) => setTimeout(resolve, POLL_MS));
      if (closed.current) return;

      let job;

      try {
        const res = await axios.get(
          `${API_BASE_URL}/candidates/import_job/${jobId}`,
          auth,
        );
        job = res.data?.job;
      } catch (err) {
        // One dropped request is not a failed import - the job is on the
        // server either way, so keep asking. Logged rather than swallowed:
        // a poll that fails every single time looks identical to a job that
        // never progresses, and silence is how that stays a mystery.
        console.error(
          "[import] poll failed:",
          err.response?.status,
          err.response?.data || err.message,
        );
        continue;
      }

      if (!job) continue;

      setSent(job.done || 0);

      // The server calls the second phase 'sheet'; this screen has always
      // called it statuses, and the stepper reads the latter.
      if (job.phase) setPhase(job.phase === "sheet" ? "statuses" : job.phase);

      if (job.status === "done") {
        const out =
          typeof job.result === "string"
            ? JSON.parse(job.result)
            : job.result || {};

        setResult({
          added: out.added || 0,
          failedAt: null,
          error: null,
          record: out.record || null,
          reporting: out.reporting || null,
          compensation: out.compensation || null,
        });
        setStage("done");
        toastr.success(`${out.added || 0} LinkedIn URLs sent to this role.`);
        return;
      }

      if (job.status === "failed") {
        setResult({
          added: job.done || 0,
          failedAt: null,
          error: job.error || "The import failed.",
          record: null,
          reporting: null,
        });
        setStage("done");
        return;
      }
    }
  };

  /**
   * Writes every skipped row back out as a spreadsheet, one tab per source
   * sheet, with that sheet's headers on top so the file is readable on its own.
   */
  const downloadSkipped = () => {
    const out = XLSX.utils.book_new();
    let any = false;

    plan.perSheet.forEach(({ sheet, skipped }) => {
      if (skipped.length === 0) return;
      any = true;

      const rows = [
        ["Excel row", ...sheet.columns.map((col) => col.label || col.letter)],
        ...skipped.map((row) => [row.number, ...row.cells]),
      ];

      // Excel tab names cap at 31 characters and cannot hold : \ / ? * [ ]
      const tab = sheet.name.replace(/[:\\/?*[\]]/g, "-").slice(0, 31);
      XLSX.utils.book_append_sheet(out, XLSX.utils.aoa_to_sheet(rows), tab);
    });

    if (!any) {
      toastr.info("Nothing was skipped.");
      return;
    }

    XLSX.writeFile(out, `skipped-rows-${book.fileName}`);
  };

  /**
   * The people PINE would not let the import place, joined back to the sheet.
   *
   * The server answers in LinkedIn URLs because that is all it was given. The
   * sheet's own wording and row number live here in the browser, so the join
   * happens here rather than sending the sheet to the server to get it back.
   *
   * Names are deliberately absent: the LinkedIn sync has not run yet when this
   * screen appears, so PINE does not know them, and the sheet's name column is
   * not read by the import.
   */
  /**
   * Confirmed compensation, keyed by the exact wording it was read from.
   *
   * Only ticked rows are here. An untouched reading was never confirmed and
   * must not reach anything that can write it.
   */
  const confirmedCompensation = () => {
    const confirmed = new Map();

    Object.values(compReadings).forEach((list) => {
      (list || []).forEach((reading) => {
        if (reading.ignored) return;
        confirmed.set(reading.value, reading);
      });
    });

    return confirmed;
  };

  /**
   * Everyone this import touched who has a confirmed figure.
   *
   * Deliberately NOT limited to the people PINE blocked. Pay belongs to the
   * person, not to whether their status happened to need a CV - and a figure
   * confirmed for someone who imported cleanly used to be dropped on the floor.
   */
  const compensationToRecord = () => {
    if (!plan) return [];

    const confirmed = confirmedCompensation();
    if (confirmed.size === 0) return [];

    const seen = new Set();
    const out = [];

    plan.rows.forEach((row) => {
      if (!row.compensation || seen.has(row.url)) return;
      const figure = confirmed.get(row.compensation);
      if (!figure) return;

      seen.add(row.url);
      out.push({
        url: row.url,
        person: row.person || "",
        compensationSaid: row.compensation,
        compensation: figure,
      });
    });

    return out;
  };

  const worklist = () => {
    const blocked = (result && result.record && result.record.blocked) || [];
    if (blocked.length === 0) return [];

    // Present when the sheet is still open in this window. Absent when the
    // import is being read back from a finished job, which is the whole
    // point of `entry.row` below.
    const byUrl = new Map((plan?.rows || []).map((row) => [row.url, row]));

    const nameOf = new Map(
      pineStatuses
        .filter((status) => status.minor_id)
        .map((status) => [
          status.minor_id,
          `${status.master_status} · ${status.minor_status}`,
        ]),
    );

    const confirmedComp = confirmedCompensation();

    return blocked.map((entry) => {
      // The live sheet first; the copy the job kept otherwise. `entry.row`
      // uses the finish screen's own field names, so the two shapes differ
      // and are read apart below rather than merged.
      const row = byUrl.get(entry.linkedin_url) || {};
      const kept = entry.row || {};

      const saidAboutPay = row.compensation || kept.compensationSaid || "";
      const comp = saidAboutPay
        ? confirmedComp.get(saidAboutPay) || null
        : null;

      return {
        url: entry.linkedin_url,
        candidateId: entry.candidate_id,
        // What the sheet said about pay, and what the user confirmed it means.
        // `said` is kept beside the figures on purpose - the same rule the
        // review screen follows, so a wrong reading stays visible.
        compensationSaid: saidAboutPay,
        compensation: comp,
        // What the finish screen needs to save: the row linking this person to
        // this role, the status the sheet wanted as an id rather than a label,
        // and the same comment the import wrote for everyone it could place.
        candidateFilterId: entry.candidate_filter_id,
        wantedStatusId: entry.candidate_status_id,
        comment: row.url ? buildComment(row) : kept.comment || "",
        person: row.person || kept.person || "",
        said: row.status || kept.said || "",
        wanted: nameOf.get(entry.candidate_status_id) || "",
        reason: entry.reason,
        sheet: row.sheet || kept.sheet || "",
        number: row.number || kept.number || "",
      };
    });
  };

  /**
   * A starting point for someone with no sheet of their own.
   *
   * Three columns because that is everything the import reads: the link, the
   * status, and whatever note goes with it. The example rows are there to show
   * that the status column takes the consultant's own wording - it is mapped
   * onto PINE's list afterwards, and nothing has to match anything.
   *
   * This is a convenience, not a contract. The detector reads cells rather than
   * headers precisely so an existing tracker imports untouched, and renaming
   * these columns changes nothing.
   */
  const downloadTemplate = () => {
    const out = XLSX.utils.book_new();

    // The rules first, the example second. Someone opening this file wants to
    // know what is required before they look at a layout they are free to
    // ignore - the detector reads cells, not headers.
    XLSX.utils.book_append_sheet(
      out,
      XLSX.utils.aoa_to_sheet([
        ["Check list"],
        [],
        [
          "1. At least one column with LinkedIn URLs should be present.",
        ],
        [
          "2. Status and minor status (at least one) can be added for auto status update.",
        ],
        [
          "   If not added, the candidates will go to Prospective / New status.",
        ],
        ["3. There can be multiple notes columns."],
      ]),
      "Check list",
    );

    XLSX.utils.book_append_sheet(
      out,
      XLSX.utils.aoa_to_sheet([
        ["LinkedIn URL", "Status", "Notes"],
        [
          "https://www.linkedin.com/in/example-one",
          "Briefed and not interested",
          "Happy where she is, revisit next year",
        ],
        [
          "https://www.linkedin.com/in/example-two",
          "LI Req sent",
          "",
        ],
        ["https://www.linkedin.com/in/example-three", "", ""],
      ]),
      "Sample",
    );

    XLSX.writeFile(out, "pine-import-sample.xlsx");
  };

  /**
   * The rows no URL could be read from, listed rather than counted.
   *
   * A count tells the user something was left behind; the list tells them
   * whether it matters. The first non-empty cell is whatever the sheet led with
   * - usually a name - which identifies the row without assuming a name column
   * exists, and the import never reads one.
   *
   * Shown on both the ready-to-import screen and the done screen, with its own
   * download beside the heading. Returns null when nothing was skipped.
   */
  const SKIPPED_SHOWN = 12;

  const skippedPanel = () => {
    // No plan means these screens are rendering for a job read back from the
    // server rather than from a sheet open in this window. Skipped rows are a
    // fact about the spreadsheet, so there is nothing to show.
    if (!plan || plan.skippedCount === 0) return null;

    const rows = plan.perSheet.flatMap(({ sheet, skipped }) =>
      skipped.map((row) => ({
        sheet: sheet.name,
        number: row.number,
        hint: row.cells.find((cell) => cell && String(cell).trim()) || "",
      })),
    );

    return (
      <div>
        <div className="d-flex align-items-center gap-2 mb-2">
          <div className="fw-semibold small">
            {plan.skippedCount} row{plan.skippedCount === 1 ? "" : "s"} have no
            LinkedIn link and are not imported
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary ms-auto"
            style={{ fontSize: "11.5px" }}
            onClick={downloadSkipped}
          >
            Download skipped rows
          </button>
        </div>

        {/* Same shape as the worklist table above it - these are two answers to
            the same question and reading them differently is friction. */}
        <div
          className="border rounded mb-3"
          style={{ maxHeight: "220px", overflowY: "auto" }}
        >
          <table className="table table-sm mb-0 small">
            <thead>
              <tr>
                <th>Row</th>
                <th>Sheet said</th>
                <th>Why not</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, SKIPPED_SHOWN).map((row, i) => (
                <tr key={i}>
                  <td>
                    <div style={{ fontWeight: 600 }}>
                      {row.sheet} · row {row.number}
                    </div>
                  </td>
                  <td>{row.hint || "(empty)"}</td>
                  <td className="text-muted">no LinkedIn link in this row</td>
                </tr>
              ))}
              {rows.length > SKIPPED_SHOWN && (
                <tr>
                  <td colSpan={3} className="text-muted">
                    + {rows.length - SKIPPED_SHOWN} more — all of them are in
                    the download.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const downloadWorklist = () => {
    const rows = worklist();
    if (rows.length === 0) {
      toastr.info("Nothing needs finishing by hand.");
      return;
    }

    const out = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      out,
      XLSX.utils.aoa_to_sheet([
        ["Name", "LinkedIn URL", "Sheet said", "Status wanted", "Why not set", "Sheet", "Excel row"],
        ...rows.map((row) => [
          row.person,
          row.url,
          row.said,
          row.wanted,
          row.reason,
          row.sheet,
          row.number,
        ]),
      ]),
      "Needs a status",
    );

    XLSX.writeFile(out, `needs-status-${book.fileName}`);
  };

  const selectionSummary =
    selected.length === 0
      ? "No sheets selected — pick at least one to continue."
      : `${selected.length} sheet${
          selected.length === 1 ? "" : "s"
        } selected: ${selected.join(", ")}`;

  /**
   * Selected sheets the detector found no profile links in.
   *
   * An import reads nothing else, so one of these contributes no people at all.
   * Counts come from the first 200 rows, so an empty linkColumns means no URL
   * was seen in the sample - the sheet is not merely sparse, it has none to
   * find at the top.
   */
  const sheetsWithoutLinks = (book ? book.sheets : [])
    .filter(
      (sheet) =>
        selected.includes(sheet.name) &&
        (sheet.linkColumns || []).length === 0,
    )
    .map((sheet) => sheet.name);

  /* ---------------------------------------------------------------- */
  /* Step 1 - no file yet                                              */
  /* ---------------------------------------------------------------- */

  // This screen also holds a sheet already read but not yet confirmed - the
  // heading below says which of the two states it is in.
  //
  // The stage check is not optional. Reopening an unfinished import attaches
  // to it from the server, and there is no workbook in the browser then - so
  // without this, every screen after the picker was unreachable and the
  // consultant was asked for the spreadsheet again no matter what the stage
  // said.
  if (stage === "sheets" && (!book || !started)) {
    return (
      <div className="modal fade show d-block" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Import from a spreadsheet</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => onClose()}
              ></button>
            </div>

            <div className="modal-body" style={{ padding: "24px 22px" }}>
              {/* Nothing until we know whether this role already has an
                  import under way. Rendering the picker first and correcting
                  a moment later is how someone mid-import gets asked for the
                  spreadsheet again. */}
              {checking ? (
                <div
                  className="text-muted text-center py-5"
                  style={{ fontSize: "13px" }}
                >
                  Checking for an import already under way…
                </div>
              ) : othersImport ? (
                /* A colleague is importing into this role. PINE refuses a
                   second one, so offering a file picker here would only
                   waste the time it takes to map a sheet. */
                <div className="py-4">
                  <div
                    className="alert alert-warning py-3 px-3 mb-3"
                    style={{ fontSize: "13px" }}
                  >
                    <strong>
                      {othersImport.owner_name || "Someone else"} is importing
                      into this role.
                    </strong>
                    <div className="mt-1">
                      {othersImport.status === "queued"
                        ? "Their import is waiting to start."
                        : `${othersImport.done} of ${othersImport.total} people added so far.`}
                    </div>
                  </div>

                  <div className="text-muted" style={{ fontSize: "12.5px" }}>
                    Only one import can run on a role at a time — two sheets
                    writing the same search at once can undo each other's work.
                    Yours can start as soon as theirs finishes. This screen
                    updates on its own.
                  </div>
                </div>
              ) : (
              <>

              {/* A target, not a form row. The old one was a bare file input
                  under a label reading "Spreadsheet", which gave no sense of
                  what the screen was for. */}
              <div
                onDragOver={(e) => {
                  // Without preventDefault the browser navigates to the file
                  // instead of handing it over, and the drop never fires.
                  e.preventDefault();
                  if (!reading) setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragging(false);
                  if (reading) return;
                  openFile(e.dataTransfer?.files?.[0] || null);
                }}
                style={{
                  border: `2px dashed ${dragging ? ACCENT : "#dcd6cd"}`,
                  borderRadius: "10px",
                  padding: "32px 20px",
                  textAlign: "center",
                  background: dragging ? ACCENT_SOFT : "#fbfaf9",
                  opacity: reading ? 0.6 : 1,
                  transition: "border-color 120ms, background 120ms",
                }}
              >
                <svg
                  width="34"
                  height="34"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#c4bdb2"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <path d="M14 2v6h6" />
                  <path d="M9 15h6" />
                  <path d="M9 11h2" />
                </svg>

                <div
                  style={{
                    marginTop: "12px",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#1c1917",
                  }}
                >
                  {reading
                    ? `Reading ${file?.name}…`
                    : dragging
                      ? "Drop it here"
                      : book
                        ? file?.name || "Spreadsheet ready"
                        : "Drop a spreadsheet here"}
                </div>
                <div
                  style={{ marginTop: "4px", fontSize: "12.5px", color: "#78716b" }}
                >
                  {book
                    ? `${book.sheets.length} sheet${book.sheets.length === 1 ? "" : "s"} read — tick the box below, then Next`
                    : ".xlsx or .xlsm"}
                </div>

                {/* The button, not the whole panel. A panel that is entirely
                    clickable gives no target to aim at, and a drop zone that
                    also swallows clicks is two behaviours on one surface. */}
                <label
                  htmlFor="pm-excel-file"
                  className="btn btn-primary btn-sm"
                  style={{
                    marginTop: "14px",
                    fontSize: "12.5px",
                    fontWeight: 600,
                    padding: "7px 17px",
                    cursor: reading ? "default" : "pointer",
                    marginBottom: 0,
                  }}
                >
                  {book ? "Choose a different file" : "Choose a file"}
                </label>

                <input
                  id="pm-excel-file"
                  type="file"
                  className="d-none"
                  accept={ACCEPT}
                  onChange={pickFile}
                  disabled={reading}
                />
              </div>

              <div
                style={{
                  marginTop: "16px",
                  fontSize: "12px",
                  color: "#78716b",
                  lineHeight: 1.6,
                  textWrap: "pretty",
                }}
              >
                The file is read on this computer and never uploaded. Any layout
                works — columns are found by what is inside them, not by what
                they are called. LinkedIn links come across always; the sheet&rsquo;s
                status and notes if you want them.
              </div>

              {/* For someone starting from nothing. Deliberately NOT a required
                  format - the detector reads cells, not headers, and a real
                  consultant's tracker imports as it is. */}
              <div style={{ marginTop: "10px", fontSize: "12px" }}>
                Starting from scratch?{" "}
                <button
                  type="button"
                  className="btn btn-link p-0 align-baseline"
                  style={{ fontSize: "12px" }}
                  onClick={downloadTemplate}
                >
                  Download a sample sheet
                </button>
              </div>

              {/* The gate. Nothing can be chosen until this is ticked. */}
              <div className="form-check" style={{ marginTop: "14px" }}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="pm-excel-format-ok"
                  checked={formatConfirmed}
                  onChange={(e) => {
                    setFormatConfirmed(e.target.checked);
                    if (e.target.checked) setGateWarning(false);
                  }}
                />
                <label
                  className="form-check-label"
                  htmlFor="pm-excel-format-ok"
                  style={{ fontSize: "12px", color: "#57534e" }}
                >
                  I have checked my sheet and it has a column of LinkedIn
                  profile links.
                </label>

                {gateWarning && !formatConfirmed && (
                  <div
                    style={{
                      marginTop: "4px",
                      fontSize: "12px",
                      color: "#b91c1c",
                    }}
                  >
                    Tick this before clicking Next.
                  </div>
                )}
              </div>
              </>
              )}
            </div>

            <div
              className="modal-footer"
              style={{
                gap: "10px",
                display: checking || othersImport ? "none" : undefined,
              }}
            >
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => onClose()}
              >
                Cancel
              </button>
              {/* Always clickable. A disabled button says nothing about why -
                  pressing this one puts the reason next to the thing that has
                  to be done. */}
              <button
                type="button"
                className="btn btn-primary"
                style={{ background: ACCENT }}
                disabled={reading}
                onClick={() => {
                  if (!book) {
                    toastr.info("Choose a spreadsheet first.");
                    return;
                  }
                  if (!formatConfirmed) {
                    setGateWarning(true);
                    return;
                  }
                  setStarted(true);
                }}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /* The preview grid, shared by the sheet and column screens          */
  /* ---------------------------------------------------------------- */

  /**
   * The sheet as a spreadsheet, with each column tinted by the job it has been
   * given. Naming a column in a chip is not enough - the user has to see what
   * is actually in it before agreeing that "Status" is the status.
   *
   * roles = { link, status, notes } of absolute column indexes; any may be
   * empty, and a column with no role renders plain.
   *
   * roleControl is optional. Given one, the header carries it instead of a
   * static role label - that is what turns the grid from a preview into the
   * place where columns are assigned, so the cells beneath a choice are always
   * visible while it is being made.
   */
  const renderPreviewGrid = (sheet, roles, roleControl) => {
    const cols = showHidden
      ? sheet.columns
      : sheet.columns.filter((col) => !col.hidden);

    const roleOf = (index) => {
      if ((roles.link || []).includes(index)) return ROLE.link;
      if ((roles.status || []).includes(index)) return ROLE.status;
      if ((roles.notes || []).includes(index)) return ROLE.notes;
      if ((roles.compensation || []).includes(index)) return ROLE.compensation;
      return null;
    };

    return (
      <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <div style={{ display: "inline-block", minWidth: "100%" }}>
          {/* column letters */}
          <div
            style={{
              display: "flex",
              position: "sticky",
              top: 0,
              zIndex: 3,
              background: "#fff",
            }}
          >
            <div style={cellGutter(28)} />
            {cols.map((col) => (
              <div key={col.index} style={cellLetter()}>
                {col.letter}
              </div>
            ))}
          </div>

          {/* The header band is tinted, so it needs an opaque layer under it -
              otherwise the data rows scrolling beneath show straight through. */}
          <div
            style={{
              display: "flex",
              position: "sticky",
              top: "28px",
              zIndex: 3,
              background: "#fff",
            }}
          >
            <div style={{ ...cellGutter(58), fontWeight: 700, color: "#78716b" }}>
              {sheet.headerRow + 1}
            </div>
            {cols.map((col) => {
              const role = roleOf(col.index);
              return (
                <div
                  key={col.index}
                  style={{
                    // See cellLetter - the three have to size identically.
                    flex: `1 0 ${COL_WIDTH}px`,
                    minWidth: `${COL_WIDTH}px`,
                    // Sized to what is actually in the cell: 18px of padding,
                    // a label line, and the 26px dropdown with its margin.
                    // It was 78px, which forced a band of empty space under
                    // every dropdown. The row still grows for the columns
                    // that carry a "filled from LinkedIn" line underneath.
                    minHeight: roleControl ? "64px" : "58px",
                    background: role ? role.soft : "#faf9f7",
                    borderRight: "1px solid #f0ece7",
                    borderBottom: `2px solid ${role ? role.line : "#eae6e1"}`,
                    padding: "9px 10px",
                    fontSize: "11.5px",
                    fontWeight: 700,
                    color: "#44403c",
                    lineHeight: 1.3,
                  }}
                >
                  <div
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={col.label}
                  >
                    {col.label || (
                      <span style={{ color: "#c7c0b8", fontWeight: 400 }}>
                        no header
                      </span>
                    )}
                  </div>

                  {roleControl ? (
                    roleControl(col, role)
                  ) : (
                    role && (
                      <div
                        style={{
                          marginTop: "4px",
                          fontSize: "10px",
                          fontWeight: 700,
                          color: role.ink,
                          letterSpacing: "0.04em",
                        }}
                      >
                        {role.label}
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>

          {/* data rows */}
          {sheet.previewRows.map((row) => (
            <div key={row.number} style={{ display: "flex" }}>
              <div style={cellGutter(40)}>{row.number}</div>
              {cols.map((col) => {
                const value = row.cells[col.pos];
                const role = roleOf(col.index);
                return (
                  <div
                    key={col.index}
                    style={{
                      // See cellLetter - the three have to size identically.
                      flex: `1 0 ${COL_WIDTH}px`,
                      minWidth: `${COL_WIDTH}px`,
                      height: "40px",
                      background: role ? role.soft : "#fff",
                      borderRight: "1px solid #f4f1ed",
                      borderBottom: "1px solid #f0ece7",
                      padding: "0 10px",
                      display: "flex",
                      alignItems: "center",
                      fontSize: "12.5px",
                      color: value ? "#292524" : "#c7c0b8",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={value}
                  >
                    {value || "—"}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  /* ---------------------------------------------------------------- */
  /* Step 3 - which columns hold the status and the notes              */
  /* ---------------------------------------------------------------- */

  if (stage === "columns") {
    const picked = chosenSheets();
    const sheet = picked.includes(current) ? current : picked[0];

    const preview = extractRowData(
      book.workbook.Sheets[sheet.name],
      sheet.columns,
      {
        link: linkIndexes(sheet),
        status: statusIndexes(sheet),
        notes: notesIndexes(sheet),
      },
    );
    const distinct = distinctStatuses(preview.rows);
    const withStatus = preview.rows.filter((row) => row.status).length;

    /**
     * The control that sits on each column heading.
     *
     * The whole point of the screen: the choice is made on the column, with
     * that column's own cells directly underneath it. Naming a column in a side
     * panel asked the user to match a name to a heading by eye, and never
     * showed what the column actually held - which is how a column of LinkedIn
     * URLs could be picked as notes without anyone noticing.
     */
    const roleControl = (col, role) => (
      <div style={{ marginTop: "5px" }}>
        <select
          className="form-select form-select-sm"
          style={{
            height: "26px",
            padding: "0 22px 0 7px",
            fontSize: "10px",
            fontWeight: 700,
            letterSpacing: "0.04em",
            color: role ? role.ink : "#8a837c",
            borderColor: role ? role.line : "#eae6e1",
            // backgroundColor, not background: the shorthand wipes the caret
            // image form-select paints, and without it nothing on the heading
            // says it can be changed.
            backgroundColor: role ? "#fff" : "#f7f5f2",
          }}
          value={roleOfColumn(sheet, col.index)}
          onChange={(e) => setColumnRole(sheet, col.index, e.target.value)}
        >
          <option value="none">NOT USED</option>
          <option value="link">LINKEDIN</option>
          <option value="status">STATUS</option>
          <option value="notes">NOTES</option>
          <option value="compensation">COMPENSATION</option>
        </select>

        {/* The flow has never explained why it ignores columns the user can
            plainly see. The sync overwrites them, and saying so stops people
            labelling them in the hope it helps. */}
        {!role && (col.kind === "person" || col.kind === "company") && (
          <div
            style={{
              marginTop: "3px",
              fontSize: "9.5px",
              fontWeight: 400,
              color: "#a8a29b",
            }}
          >
            filled from LinkedIn
          </div>
        )}
      </div>
    );

    return (
      <div className="modal fade show d-block" tabIndex="-1">
        <div
          className="modal-dialog modal-dialog-centered"
          style={{ maxWidth: "1290px", width: "calc(100% - 48px)" }}
        >
          <div
            className="modal-content"
            style={{ height: "calc(100vh - 76px)", maxHeight: "790px" }}
          >
            <div
              className="flex-shrink-0 d-flex align-items-start gap-3"
              style={{ padding: "20px 26px 18px", borderBottom: "1px solid #eeeae5" }}
            >
              <div>
                <div style={{ fontSize: "19px", fontWeight: 700, color: "#1c1917" }}>
                  Status and notes
                </div>
                <div style={{ marginTop: "6px", fontSize: "13px", color: "#78716b" }}>
                  {stepLabel("columns")} · Check the highlighted columns against
                  the sheet
                </div>
                {/* The accent, not the muted grey the rest of the subtitle
                    uses. Nothing else on this screen says the headings can be
                    changed, and in grey nobody read it. */}
                <div
                  style={{
                    marginTop: "4px",
                    fontSize: "12.5px",
                    color: ACCENT,
                    fontWeight: 600,
                  }}
                >
                  Every column has a dropdown above it — change anything that
                  is wrong, or choose NOT USED to leave a column out. PINE
                  picked these by reading the cells, not the column names.
                </div>
              </div>

              {/* No progress bar. It was four hardcoded segments on every
                  screen, so it claimed four steps while the subtitle counted
                  five - and the real number varies, because a sheet with no
                  status or no compensation column skips those steps entirely.
                  stepLabel() works that out and is the only honest count. */}
              <div className="ms-auto d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn-close ms-2"
                  onClick={() => onClose()}
                ></button>
              </div>
            </div>

            <div
              className="flex-grow-1 d-flex flex-column"
              style={{ minHeight: 0, background: "#fbfaf9" }}
            >
              {/* Which sheet, and what each still needs. Above the grid, not
                  below it: as tabs at the bottom they were missable, and a
                  sheet nobody had configured looked the same as a finished
                  one. */}
              <div
                className="flex-shrink-0 d-flex align-items-center gap-2 flex-wrap"
                style={{
                  padding: "10px 22px",
                  background: "#faf9f7",
                  borderBottom: "1px solid #eeeae5",
                }}
              >
                {picked.map((one) => {
                  const on = one.name === sheet.name;
                  const needsStatus =
                    statusIndexes(one).length === 0 &&
                    one.statusColumns.length === 0;

                  return (
                    <div
                      key={one.name}
                      role="button"
                      onClick={() => setActive(sheets.indexOf(one))}
                      style={{
                        height: "32px",
                        padding: "0 12px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        background: "#fff",
                        borderRadius: "7px",
                        border:
                          "1px solid " +
                          (on ? ACCENT : needsStatus ? "#e6c9a8" : "#eae6e1"),
                      }}
                    >
                      <span
                        style={{
                          fontSize: "12.5px",
                          fontWeight: on ? 700 : 400,
                          color: on ? "#1c1917" : "#57534e",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {one.name}
                      </span>
                      <span className="d-flex gap-1">
                        {[
                          ["link", linkIndexes(one)],
                          ["status", statusIndexes(one)],
                          ["notes", notesIndexes(one)],
                          ["compensation", compIndexes(one)],
                        ].map(([key, list]) => (
                          <span
                            key={key}
                            title={ROLE[key].label}
                            style={{
                              width: "7px",
                              height: "7px",
                              // Square, matching the key on the right of this
                              // same row. Round here and square there read as
                              // two different things.
                              borderRadius: 0,
                              background: list.length
                                ? ROLE[key].ink
                                : "#eae6e1",
                            }}
                          />
                        ))}
                      </span>
                      {needsStatus && (
                        <span
                          style={{
                            fontSize: "10px",
                            fontWeight: 600,
                            color: "#8a5a1b",
                            whiteSpace: "nowrap",
                          }}
                        >
                          no status found
                        </span>
                      )}
                    </div>
                  );
                })}

                {/* All four roles. COMPENSATION was left out when it was added
                    as a fourth, so a purple column on the grid matched nothing
                    in the key. Square markers, because the tint they stand for
                    is painted as a rectangle behind the column. */}
                <div className="ms-auto d-flex align-items-center gap-3">
                  {[ROLE.link, ROLE.status, ROLE.notes, ROLE.compensation].map(
                    (role) => (
                      <span
                        key={role.label}
                        style={{
                          fontSize: "10px",
                          fontWeight: 700,
                          color: role.ink,
                          letterSpacing: "0.05em",
                        }}
                      >
                        <span
                          className="d-inline-block me-1"
                          style={{
                            width: "7px",
                            height: "7px",
                            borderRadius: 0,
                            background: role.ink,
                            verticalAlign: "0",
                          }}
                        />
                        {role.label}
                      </span>
                    ),
                  )}
                </div>
              </div>

              {/* Detection finding nothing is a supported outcome, not an
                  error - the sheet still imports at PINE's default. */}
              {sheet.statusColumns.length === 0 &&
                statusIndexes(sheet).length === 0 && (
                  <div
                    className="flex-shrink-0 d-flex gap-2"
                    style={{
                      margin: "12px 22px 0",
                      padding: "11px 14px",
                      border: "1px solid #e6c9a8",
                      background: "#fdf8f2",
                      borderRadius: "8px",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: "12.5px",
                          fontWeight: 600,
                          color: "#7a4c14",
                        }}
                      >
                        No status column found on this sheet.
                      </div>
                      <div
                        style={{
                          marginTop: "3px",
                          fontSize: "12px",
                          color: "#8a5a1b",
                          lineHeight: 1.55,
                        }}
                      >
                        A status column repeats itself — the same few words
                        across many rows. Nothing here does. Label one yourself
                        if we missed it, or continue and these{" "}
                        {preview.rows.length} people arrive at Prospective /
                        New.
                      </div>
                    </div>
                  </div>
                )}

              {renderPreviewGrid(
                sheet,
                {
                  link: linkIndexes(sheet),
                  status: statusIndexes(sheet),
                  notes: notesIndexes(sheet),
                  compensation: compIndexes(sheet),
                },
                roleControl,
              )}
            </div>

            <div
              className="flex-shrink-0 d-flex align-items-center gap-2"
              style={{
                padding: "14px 22px",
                borderTop: "1px solid #eeeae5",
                background: "#fff",
              }}
            >
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => goBack("columns")}
              >
                Back
              </button>

              {/* Moved out of the side panel, which is gone. It belongs next to
                  Continue, because it is what Continue will act on. */}
              <div
                style={{
                  fontSize: "12.5px",
                  color: distinct.length ? "#57534e" : "#a8a29b",
                  marginLeft: "14px",
                }}
              >
                {distinct.length === 0
                  ? `${preview.rows.length} people on ${sheet.name} · no status to map`
                  : `${withStatus} of ${preview.rows.length} on ${sheet.name} have a status · ${distinct.length} wordings to map`}
              </div>

              <div className="ms-auto d-flex gap-2">
                <StepButton
                  className="btn btn-outline-secondary"
                  busy={confirmBusy}
                  busyLabel="Checking PINE..."
                  disabled={mappingBusy || compBusy}
                  // "Links only" says what it leaves out but not what it
                  // skips, and the two steps it skips cost money and time -
                  // worth knowing before pressing it rather than after.
                  title={
                    "Add these people to the role and stop there. Skips the " +
                    "status, compensation and call-note steps, so everyone " +
                    "lands at Prospective / New with nothing else recorded."
                  }
                  onClick={() => goToConfirm(true)}
                >
                  Links only
                </StepButton>
                <StepButton
                  // Continue leads to the status mapping, which may fall
                  // straight through to compensation and then to the status
                  // check - so all three flags keep it busy.
                  busy={mappingBusy || compBusy || confirmBusy}
                  busyLabel={
                    mappingBusy
                      ? "Reading statuses..."
                      : compBusy
                        ? "Reading compensation..."
                        : "Checking PINE..."
                  }
                  onClick={goToMapping}
                >
                  Continue
                </StepButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /* Step 4 - check the mapping                                        */
  /* ---------------------------------------------------------------- */

  if (stage === "mapping") {
    const sheetNames = Object.keys(mapping);

    const setStatusFor = (sheetName, value, statusId) => {
      setMapping({
        ...mapping,
        [sheetName]: mapping[sheetName].map((row) =>
          row.value === value
            ? { ...row, statusId, source: "you", confidence: 10, note: "" }
            : row,
        ),
      });
    };

    const rowsNeedingAFile = sheetNames.reduce(
      (sum, name) =>
        sum +
        mapping[name]
          .filter((row) => NEEDS_A_FILE.has(row.statusId))
          .reduce((n, row) => n + row.rows, 0),
      0,
    );

    return (
      <div className="modal fade show d-block" tabIndex="-1">
        <div
          className="modal-dialog modal-dialog-centered"
          style={{ maxWidth: "1100px", width: "calc(100% - 48px)" }}
        >
          <div
            className="modal-content"
            style={{ height: "calc(100vh - 76px)", maxHeight: "790px" }}
          >
            <div
              className="flex-shrink-0 d-flex align-items-start gap-3"
              style={{ padding: "20px 26px 18px", borderBottom: "1px solid #eeeae5" }}
            >
              <div>
                <div style={{ fontSize: "19px", fontWeight: 700 }}>
                  Check the mapping
                </div>
                <div style={{ marginTop: "6px", fontSize: "13px", color: "#78716b" }}>
                  {stepLabel("mapping")} · What your sheet said, and the PINE
                  status each
                  one will become.
                </div>
                {/* Same treatment as the column screen: the sentence that says
                    something can be changed is the one people miss. */}
                <div
                  style={{
                    marginTop: "4px",
                    fontSize: "12.5px",
                    color: ACCENT,
                    fontWeight: 600,
                  }}
                >
                  Change any that look wrong. The least certain are at the top,
                  and the names under each wording show who it applies to.
                </div>
              </div>

              {/* Every step carries one. Nothing has been written at this
                  point - the import runs from the confirm screen - so leaving
                  costs the user their choices and nothing else. */}
              <button
                type="button"
                className="btn-close ms-auto"
                aria-label="Close"
                onClick={() => onClose()}
              />
            </div>

            <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "18px 26px" }}>
              {/* No loading state here. This screen is not reached until the
                  mapping is in hand - the wait happens in the Continue button
                  on the previous screen. */}
              {sheetNames.length === 0 && (
                <div className="text-muted small">
                  Nothing to map on the selected sheets.
                </div>
              )}

              {sheetNames.map((name) => (
                <div key={name} style={{ marginBottom: "22px" }}>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: 700,
                      marginBottom: "8px",
                    }}
                  >
                    {name}
                    <span className="text-muted fw-normal">
                      {" "}· {mapping[name].length} wordings
                    </span>
                  </div>

                  {/* A grid, not a table. The table let the first column
                      collapse under pressure and clipped the wording mid-word
                      on a narrow modal - and these wordings run long:
                      "Connected (LI) - not interested in changing jobs - tried
                      to open conversation /asked for a quick call - awaiting
                      response" is one real value. minmax() gives it a floor. */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: MAPPING_GRID,
                      gap: "14px",
                      fontSize: "10px",
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      color: "#8a837c",
                      paddingBottom: "7px",
                      borderBottom: "1px solid #eeeae5",
                    }}
                  >
                    <div>YOUR SHEET SAID</div>
                    <div>PEOPLE</div>
                    <div>IN PINE</div>
                    {/* The number is the model's own guess at its accuracy,
                        not a measurement. */}
                    <div>CONFIDENCE (1-10)</div>
                  </div>

                  {mapping[name].map((row) => {
                    const blocked = NEEDS_A_FILE.has(row.statusId);

                    return (
                      <div
                        key={row.value}
                        style={{
                          display: "grid",
                          gridTemplateColumns: MAPPING_GRID,
                          gap: "14px",
                          alignItems: "start",
                          padding: "11px 0",
                          borderBottom: "1px solid #f4f1ed",
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: "12.5px",
                              color: "#1c1917",
                              lineHeight: 1.35,
                              textWrap: "pretty",
                            }}
                          >
                            {row.value}
                          </div>

                          {row.note && (
                            <div
                              style={{
                                marginTop: "3px",
                                fontSize: "11px",
                                color: "#8a837c",
                                lineHeight: 1.35,
                              }}
                            >
                              {row.note}
                            </div>
                          )}

                          {/* Who this wording covers. One wording can cover
                              eight people, and ruling on it without seeing them
                              is guesswork. Falls back to the sheet row when the
                              sheet has no name column - several real ones do
                              not. */}
                          {row.people && row.people.length > 0 && (
                            <div className="d-flex flex-wrap gap-1 mt-2">
                              {/* The chip opens their LinkedIn. Deciding what a
                                  wording means often comes down to who it is
                                  about, and the sheet gives a name at best -
                                  the profile is the only way to check without
                                  leaving the import. A new tab, deliberately:
                                  this modal holds unsaved choices. */}
                              {row.people.map((who) => (
                                <a
                                  key={who.url}
                                  href={who.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  title={who.url}
                                  style={{
                                    fontSize: "10px",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                    background: "#f7f5f2",
                                    border: "1px solid #eeeae5",
                                    color: ACCENT,
                                    whiteSpace: "nowrap",
                                    textDecoration: "none",
                                  }}
                                >
                                  {who.person || `row ${who.number}`}
                                </a>
                              ))}
                              {row.rows > row.people.length && (
                                <span
                                  style={{
                                    fontSize: "10px",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                    border: "1px dashed #ddd8d1",
                                    color: "#8a837c",
                                  }}
                                >
                                  + {row.rows - row.people.length} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        <div style={{ fontSize: "12.5px", color: "#78716b", paddingTop: "1px" }}>
                          {row.rows}
                        </div>

                        <div style={{ minWidth: 0 }}>
                          <select
                            className="form-select form-select-sm"
                            style={{ fontSize: "12px" }}
                            value={row.statusId === null ? "" : row.statusId}
                            onChange={(e) =>
                              setStatusFor(
                                name,
                                row.value,
                                e.target.value === ""
                                  ? null
                                  : Number(e.target.value),
                              )
                            }
                          >
                            <option value="">
                              — leave at Prospective / New —
                            </option>
                            {pineStatuses
                              .filter((s) => s.minor_id)
                              .map((s) => (
                                <option key={s.minor_id} value={s.minor_id}>
                                  {s.master_status} · {s.minor_status}
                                  {NEEDS_A_FILE.has(s.minor_id)
                                    ? "  (needs a CV)"
                                    : ""}
                                </option>
                              ))}
                          </select>

                          {blocked && (
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#8a5a1b",
                                marginTop: "4px",
                                lineHeight: 1.35,
                              }}
                            >
                              This status needs a CV on file
                              {row.statusId === 48
                                ? " and a background document"
                                : ""}
                              , which a spreadsheet cannot supply.{" "}
                              {row.rows === 1
                                ? "This person is"
                                : `These ${row.rows} people are`}{" "}
                              still added to the role and their sheet wording is
                              recorded, but they stay at Prospective / New until
                              a file is attached. You get the list when the
                              import finishes.
                            </div>
                          )}
                        </div>

                        <div
                          style={{
                            fontSize: "12.5px",
                            fontWeight: 700,
                            paddingTop: "2px",
                            // Two colours, not three. The middle band read as a
                            // third verdict when it only ever meant "read this
                            // one yourself", which is true of anything under 5.
                            color: row.confidence >= 5 ? "#3f6b46" : "#b91c1c",
                          }}
                        >
                          {row.source === "you" ? "yours" : row.confidence}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            <div
              className="flex-shrink-0 d-flex align-items-center gap-2"
              style={{
                padding: "14px 22px",
                borderTop: "1px solid #eeeae5",
                background: "#fff",
              }}
            >
              <div style={{ fontSize: "12.5px", color: "#78716b" }}>
                {rowsNeedingAFile > 0
                  ? `${rowsNeedingAFile} people will need their status set by hand.`
                  : "Every mapped status can be set automatically."}
              </div>
              <div className="ms-auto d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => goBack("mapping")}
                >
                  Back
                </button>
                <StepButton
                  // goToCompensation may fall straight through to goToConfirm
                  // without changing screen, so confirmBusy counts here too -
                  // otherwise this button looks idle while PINE is being asked
                  // about everyone on the sheet.
                  busy={compBusy || confirmBusy}
                  busyLabel={
                    compBusy ? "Reading compensation..." : "Checking PINE..."
                  }
                  disabled={mappingBusy}
                  onClick={() => goToCompensation(false)}
                >
                  Continue
                </StepButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /* Step 5 - check the compensation                                   */
  /* ---------------------------------------------------------------- */

  if (stage === "compensation") {
    return (
      <div className="modal fade show d-block" tabIndex="-1">
        <div
          className="modal-dialog modal-xl modal-dialog-centered"
          style={{ maxWidth: "min(1180px, 96vw)" }}
        >
          <div
            className="modal-content d-flex flex-column"
            style={{ height: "min(86vh, 860px)", overflow: "hidden" }}
          >
            <CompensationReview
              readings={compReadings}
              currencies={currencies}
              continuing={confirmBusy}
              onChange={(sheet, value, patch) =>
                setCompReadings((prev) => ({
                  ...prev,
                  [sheet]: (prev[sheet] || []).map((row) =>
                    row.value === value ? { ...row, ...patch } : row,
                  ),
                }))
              }
              onBack={() => goBack("compensation")}
              onContinue={() => goToConfirm(false)}
              onClose={() => onClose()}
            />
          </div>
        </div>
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /* Confirm / progress / summary                                      */
  /* ---------------------------------------------------------------- */

  if (stage !== "sheets") {
    // `total` rather than plan.urls.length: these screens also render for an
    // import picked up after the window was closed, when there is no
    // spreadsheet loaded in this browser at all.
    const headcount = total || plan?.urls?.length || 0;
    const pct = headcount ? Math.round((sent / headcount) * 100) : 0;

    return (
      <div className="modal fade show d-block" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {stage === "confirm" && "Ready to import"}
                {stage === "importing" && "Importing…"}
                {stage === "done" && "Import finished"}
                {stage === "finish" && "Finish these"}
              </h5>
              {/* Closable during an import, which it did not used to be.
                  The job runs on the server and is not listening to this
                  window - holding the consultant here achieved nothing
                  except making them think they had to stay. */}
              {!compRecording && (
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => onClose()}
                ></button>
              )}
            </div>

            <div className="modal-body">
              {/* Visible on every screen except the one already showing this
                  import's own progress. Answers the question the user would
                  otherwise answer by starting a second import. */}
              {liveJob && stage !== "importing" && (
                <div
                  className="alert alert-warning py-2 px-3 mb-3"
                  style={{ fontSize: "13px" }}
                >
                  An import is already running on this role —{" "}
                  <strong>
                    {liveJob.done} of {liveJob.total}
                  </strong>{" "}
                  people added
                  {liveJob.phase === "sheet" && ", now writing statuses"}
                  {liveJob.phase === "reporting" && ", now reading call notes"}
                  {liveJob.phase === "compensation" &&
                    ", now finding employers"}
                  . It carries on whether this window is open or not.
                </div>
              )}

              {stage === "confirm" && (
                <>
                  {(() => {
                    const f = statusForecast();
                    const total = plan.urls.length || 1;

                    // Each outcome, in the order they are shown. The bar and
                    // the cells read from the same list so they can never
                    // disagree.
                    const outcomes = [
                      {
                        n: f.moved,
                        ink: "#3f6b46",
                        label: "move to a new status",
                        why: "",
                      },
                      {
                        n: f.byHand,
                        ink: "#c8912f",
                        label: "need you afterwards",
                        why: "PINE wants a CV or a date",
                      },
                      {
                        n: f.alreadyNew,
                        ink: "#c8cfc6",
                        label: "stay at Prospective / New",
                        why: "the sheet put them there too",
                      },
                      {
                        n: f.unmapped,
                        ink: "#d6d1ca",
                        label: "wording kept, status unchanged",
                        // Naming the wording beats "could not be mapped" -
                        // the words are the only thing the user can act on.
                        why: f.unmappedExample
                          ? `“${f.unmappedExample}” has no PINE equivalent`
                          : "",
                      },
                      {
                        n: f.noStatus,
                        ink: "#e4e0da",
                        label: "had no status in the sheet",
                        why: "",
                      },
                    ];

                    return (
                      <>
                        <div style={{ fontSize: "15px", lineHeight: 1.5 }}>
                          <strong>{plan.urls.length} people</strong> go onto this
                          role from{" "}
                          <strong>
                            {plan.perSheet.map((s) => s.sheet.name).join(", ")}
                          </strong>
                          .
                        </div>

                        {/* The shape of the outcome, before any number is
                            read. */}
                        <div
                          className="d-flex mt-3"
                          style={{
                            height: "9px",
                            borderRadius: "5px",
                            overflow: "hidden",
                          }}
                        >
                          {outcomes
                            .filter((o) => o.n > 0)
                            .map((o) => (
                              <div
                                key={o.label}
                                title={`${o.n} ${o.label}`}
                                style={{
                                  width: `${(o.n / total) * 100}%`,
                                  background: o.ink,
                                }}
                              />
                            ))}
                        </div>

                        <div
                          className="mt-3"
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "13px 22px",
                          }}
                        >
                          {outcomes
                            .filter((o) => o.n > 0)
                            .map((o) => (
                              <div
                                key={o.label}
                                className="d-flex gap-2"
                                style={{ alignItems: "flex-start" }}
                              >
                                <span
                                  style={{
                                    width: "9px",
                                    height: "9px",
                                    borderRadius: "3px",
                                    flex: "none",
                                    marginTop: "5px",
                                    background: o.ink,
                                  }}
                                />
                                <div>
                                  <div
                                    style={{
                                      fontSize: "15px",
                                      fontWeight: 700,
                                      color: "#1c1917",
                                      fontVariantNumeric: "tabular-nums",
                                    }}
                                  >
                                    {o.n}
                                  </div>
                                  <div
                                    style={{ fontSize: "12px", color: "#57534e" }}
                                  >
                                    {o.label}
                                  </div>
                                  {o.why && (
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#8a837c",
                                        lineHeight: 1.4,
                                        marginTop: "1px",
                                      }}
                                    >
                                      {o.why}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                        </div>
                      </>
                    );
                  })()}

                  {/* Only when the sheet and PINE actually disagree. A first
                      import onto a role nobody is on has nothing to ask, and
                      this section does not appear at all. */}
                  {(() => {
                    const clashes = conflictsFor(plan, currentStatuses);
                    if (clashes.length === 0) return null;

                    const chosen = clashes.filter(
                      (row) => conflictChoice[row.url],
                    ).length;

                    const setAll = (useSheet) => {
                      const next = { ...conflictChoice };
                      clashes.forEach((row) => {
                        next[row.url] = useSheet;
                      });
                      setConflictChoice(next);
                    };

                    return (
                      <div
                        className="mt-4 pt-3"
                        style={{ borderTop: "1px solid #f0ece7" }}
                      >
                        <div className="d-flex align-items-baseline gap-2">
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: 700,
                              color: "#1c1917",
                            }}
                          >
                            {clashes.length}{" "}
                            {clashes.length === 1 ? "person has" : "people have"}{" "}
                            a different status in PINE
                          </div>
                          <div className="ms-auto d-flex gap-2">
                            <button
                              type="button"
                              className="btn btn-link p-0"
                              style={{ fontSize: "11.5px" }}
                              onClick={() => setAll(true)}
                            >
                              use the sheet&rsquo;s for all
                            </button>
                            <span style={{ color: "#ddd8d1" }}>|</span>
                            <button
                              type="button"
                              className="btn btn-link p-0 text-muted"
                              style={{ fontSize: "11.5px" }}
                              onClick={() => setAll(false)}
                            >
                              keep PINE&rsquo;s for all
                            </button>
                          </div>
                        </div>

                        <div
                          style={{
                            fontSize: "11.5px",
                            color: "#8a837c",
                            marginTop: "2px",
                          }}
                        >
                          PINE only changes a status you agree to change. The
                          rest keep what they have and are listed afterwards.
                        </div>

                        <div
                          style={{
                            marginTop: "10px",
                            maxHeight: "210px",
                            overflowY: "auto",
                          }}
                        >
                          {clashes.map((row) => (
                            <div
                              key={row.url}
                              className="d-flex align-items-start gap-3"
                              style={{
                                padding: "8px 0",
                                borderBottom: "1px solid #f4f1ed",
                              }}
                            >
                              <div style={{ minWidth: 0, flex: 1 }}>
                                {/* The sheet may have no name column, which is
                                    normal - several real files do not. The row
                                    number is what we always have. */}
                                <div
                                  style={{
                                    fontSize: "12.5px",
                                    color: "#1c1917",
                                  }}
                                >
                                  {row.person || `row ${row.number}`}
                                </div>
                                <div
                                  style={{
                                    fontSize: "11px",
                                    color: "#78716b",
                                    lineHeight: 1.5,
                                    marginTop: "2px",
                                  }}
                                >
                                  <span style={{ color: "#a8a29b" }}>PINE</span>{" "}
                                  {statusLabel(row.currentStatusId)}
                                  <br />
                                  <span style={{ color: "#a8a29b" }}>
                                    sheet
                                  </span>{" "}
                                  {statusLabel(row.wantedStatusId)}
                                </div>
                              </div>

                              <select
                                className="form-select form-select-sm"
                                style={{ width: "150px", fontSize: "11.5px" }}
                                value={conflictChoice[row.url] ? "sheet" : "pine"}
                                onChange={(e) =>
                                  setConflictChoice({
                                    ...conflictChoice,
                                    [row.url]: e.target.value === "sheet",
                                  })
                                }
                              >
                                <option value="sheet">use the sheet&rsquo;s</option>
                                <option value="pine">keep PINE&rsquo;s</option>
                              </select>
                            </div>
                          ))}
                        </div>

                        <div
                          style={{
                            fontSize: "11.5px",
                            color: "#57534e",
                            marginTop: "8px",
                          }}
                        >
                          {chosen} of {clashes.length} will be changed to what
                          the sheet says.
                        </div>
                      </div>
                    );
                  })()}

                  <div
                    className="mt-4 pt-3 text-muted"
                    style={{
                      borderTop: "1px solid #f0ece7",
                      fontSize: "12px",
                      lineHeight: 1.6,
                    }}
                  >
                    {plan?.duplicates > 0 && (
                      <div>
                        {plan?.duplicates} duplicate link
                        {plan?.duplicates === 1 ? "" : "s"} removed before
                        sending.
                      </div>
                    )}
                    {skippedPanel()}
                    {statusCheckFailed && (
                      <div className="mt-1" style={{ color: "#8a5a1b" }}>
                        Existing statuses could not be checked, so anyone already
                        moved off Prospective / New keeps the status they have.
                      </div>
                    )}
                    <div className="mt-1">
                      Names and companies arrive with the LinkedIn sync, about 20
                      minutes.
                    </div>
                  </div>
                </>
              )}

              {/* Collecting the files and dates PINE demands, for the people
                  the import refused. Its own footer, because what it offers
                  depends on how many are complete. */}
              {stage === "finish" && (
                <FinishBlocked
                  role_id={role_id}
                  people={worklist()}
                  roleContext={result?.record?.role}
                  pineStatuses={pineStatuses}
                  currencies={currencies}
                  onBack={() => setStage("done")}
                  // Done here ends the import too, not just the window. It
                  // is the other way out of the same screen, and an import
                  // left unclosed is offered again next time.
                  onClose={endImport}
                />
              )}

              {stage === "importing" && (
                <>
                  <div className="progress mb-2" style={{ height: "18px" }}>
                    <div
                      className="progress-bar"
                      role="progressbar"
                      style={{ width: `${pct}%` }}
                    >
                      {pct}%
                    </div>
                  </div>
                  <div className="text-muted small">
                    {sent} of {headcount} sent. You can close this — the
                    import carries on without you.
                  </div>

                  {/* Only the first has a progress bar. Naming the rest stops
                      them reading as a hang - the last one is a model call
                      and took 30 seconds over 160 notes when measured. */}
                  <div className="mt-3 pt-2 border-top">
                    {[
                      {
                        label: "People added to the role",
                        done: phase !== "urls",
                        active: phase === "urls",
                      },
                      {
                        label: "Writing statuses and notes",
                        done: phase === "reporting" || phase === "compensation",
                        active: phase === "statuses",
                      },
                      {
                        label: "Reading the call notes",
                        done: phase === "compensation",
                        active: phase === "reporting",
                      },
                      {
                        label: "Finding employers and recording pay",
                        done: false,
                        active: phase === "compensation",
                      },
                    ].map((step) => (
                      <div
                        key={step.label}
                        className="d-flex align-items-center gap-2 py-1"
                        style={{ fontSize: "12.5px" }}
                      >
                        <span
                          style={{
                            width: "15px",
                            height: "15px",
                            borderRadius: "8px",
                            flex: "none",
                            background: step.done ? "#eef6ee" : "transparent",
                            border: step.done
                              ? "none"
                              : step.active
                                ? "2px solid #d2691e"
                                : "1.5px solid #e4e0da",
                          }}
                        />
                        <span
                          style={{
                            color: step.active
                              ? "#1c1917"
                              : step.done
                                ? "#57534e"
                                : "#a8a29b",
                            fontWeight: step.active ? 600 : 400,
                          }}
                        >
                          {step.label}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div
                    className="text-muted mt-2"
                    style={{ fontSize: "11.5px", lineHeight: 1.55 }}
                  >
                    These are two separate steps. If the second fails, everyone
                    is still on the role at Prospective / New — nothing is left
                    half-written.
                  </div>
                </>
              )}

              {stage === "done" && (
                <>
                  {result.error ? (
                    <div className="alert alert-warning">
                      <strong>{result.added}</strong> URLs were added before the
                      import stopped at number {result.failedAt}.
                      <div className="small mt-1">{result.error}</div>
                      <div className="small mt-1">
                        Everything already added is saved. Run the import again
                        to retry — anyone already on the role is skipped.
                      </div>
                    </div>
                  ) : (
                    <div className="alert alert-success">
                      <strong>{result.added}</strong> LinkedIn URL
                      {result.added === 1 ? "" : "s"} added to this role.
                    </div>
                  )}

                  {result.record && (
                    <>
                      {result.record.error && (
                        <div className="alert alert-warning py-2">
                          <div className="small">{result.record.error}</div>
                          <div className="small mt-1">
                            Everyone above is on the role at Prospective / New.
                            Only the statuses did not finish.
                          </div>
                        </div>
                      )}

                      {/* Leads with movement, not writes. status_set counts the
                          rows mapped to Prospective / New, which were already
                          there - true, and it reads as change that did not
                          happen. */}
                      <div
                        className="mb-3"
                        style={{
                          background: "#eef6ee",
                          border: "1px solid #cfe3d0",
                          borderRadius: "7px",
                          padding: "10px 13px",
                          fontSize: "13px",
                          color: "#2f5c37",
                        }}
                      >
                        <strong>
                          {Math.max(
                            0,
                            result.record.status_set - result.record.sent_as_new,
                          )}
                        </strong>{" "}
                        people moved to a new status.
                        {result.record.sent_as_new > 0 && (
                          <span style={{ color: "#3f6b46" }}>
                            {" "}
                            {result.record.sent_as_new} were already at
                            Prospective / New, where the sheet also put them.
                          </span>
                        )}
                      </div>

                      <div className="d-flex gap-4 mb-3">
                        {[
                          {
                            n: result.record.status_set,
                            label: "statuses written",
                            ink: "#3f6b46",
                          },
                          {
                            // Blocked rows also had their wording kept, and
                            // showing them under both headings counted the same
                            // person twice.
                            n: Math.max(
                              0,
                              result.record.comments_only -
                                result.record.blocked.length,
                            ),
                            label: "wording kept, status unchanged",
                            ink: "#78716b",
                          },
                          {
                            n: result.record.blocked.length,
                            label: "need you",
                            ink: "#8a5a1b",
                          },
                          // Only shown when it happened. These people used to be
                          // counted nowhere at all - the sheet disagreed, PINE
                          // won, and the summary said nothing.
                          ...(result.record.kept_existing.length > 0
                            ? [
                                {
                                  n: result.record.kept_existing.length,
                                  label: "kept their PINE status",
                                  ink: "#78716b",
                                },
                              ]
                            : []),
                        ].map((stat) => (
                          <div key={stat.label}>
                            {/* Inline, not fs-5: the theme's stylesheet
                                overrides Bootstrap's font-size utilities inside
                                this modal and the numbers rendered too small to
                                read. */}
                            <div
                              style={{
                                fontSize: "20px",
                                fontWeight: 700,
                                lineHeight: 1.2,
                                color: stat.ink,
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {stat.n}
                            </div>
                            <div className="small text-muted">{stat.label}</div>
                          </div>
                        ))}
                      </div>

                      {/* The worker has already found each employer and written
                          each figure, so there is nothing to click here any
                          more - this used to be CompensationRecord, which held
                          the consultant for 31 to 58 seconds per person while
                          it ran a LinkedIn sync for each one. Leaving it in
                          place would now write every figure a second time.
                          Absent when no compensation column was used. */}
                      {result.compensation && (
                        <div
                          className="mb-3"
                          style={{ fontSize: "12.5px", color: "#57534e" }}
                        >
                          <strong>
                            {result.compensation.recorded} compensation{" "}
                            {result.compensation.recorded === 1
                              ? "figure"
                              : "figures"}{" "}
                            recorded.
                          </strong>

                          {result.compensation.skipped?.length > 0 && (
                            <div className="mt-1">
                              {result.compensation.skipped.length} skipped —
                              PINE does not know where these people work yet. A
                              figure has nowhere to go without an employer. The
                              profile sync reaches them within about 20 minutes,
                              and the figure can be added from the candidate
                              page after that.
                            </div>
                          )}

                          {result.compensation.failed?.length > 0 && (
                            <div className="mt-1 text-danger">
                              {result.compensation.failed.length} could not be
                              written.
                            </div>
                          )}
                        </div>
                      )}

                      {/* Already written, with nobody asked - so the only
                          honest thing is to say what was done and where to
                          look. Absent entirely when the sheet had no notes
                          column, which is the usual case. */}
                      {result.reporting && (
                        <div
                          className="mb-3"
                          style={{ fontSize: "12.5px", color: "#57534e" }}
                        >
                          {result.reporting.error ? (
                            <span style={{ color: "#8a5a1b" }}>
                              The call notes could not be read —{" "}
                              {result.reporting.error} Everything else above
                              was still imported.
                            </span>
                          ) : (
                            <>
                              <span style={{ fontWeight: 600 }}>
                                Reporting line read for{" "}
                                {result.reporting.written} of{" "}
                                {result.reporting.considered}
                              </span>{" "}
                              people with call notes. Switch on{" "}
                              <em>Reporting to</em> and <em>Team structure</em>{" "}
                              in the column menu to see them, and correct
                              anything wrong in place.
                              {result.reporting.failed > 0 && (
                                <span style={{ color: "#8a5a1b" }}>
                                  {" "}
                                  {result.reporting.failed} could not be read
                                  and were left empty.
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      )}

                      {worklist().length > 0 && (
                        <>
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <div className="fw-semibold small">
                              PINE would not set these from a spreadsheet
                            </div>
                            {/* Beside the list it describes, not in the footer.
                                The footer is for leaving the screen. */}
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary ms-auto"
                              style={{ fontSize: "11.5px" }}
                              onClick={downloadWorklist}
                            >
                              Download this list
                            </button>
                          </div>
                          <div
                            className="border rounded mb-3"
                            style={{ maxHeight: "220px", overflowY: "auto" }}
                          >
                            <table className="table table-sm mb-0 small">
                              <thead>
                                <tr>
                                  <th>Who</th>
                                  <th>Sheet said</th>
                                  <th>Why not</th>
                                </tr>
                              </thead>
                              <tbody>
                                {worklist().map((row) => (
                                  <tr key={row.url}>
                                    <td>
                                      {row.person ? (
                                        <div style={{ fontWeight: 600 }}>
                                          {row.person}
                                        </div>
                                      ) : null}
                                      <a
                                        href={row.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{ fontSize: "11px" }}
                                      >
                                        {row.sheet} · row {row.number}
                                      </a>
                                    </td>
                                    <td>
                                      {row.said || "(no status)"}
                                      <div
                                        className="text-muted"
                                        style={{ fontSize: "11px" }}
                                      >
                                        wanted {row.wanted}
                                      </div>
                                    </td>
                                    {/* No per-row link out. This screen says
                                        what happened; Finish these is the one
                                        way to act on it. Two routes to the same
                                        job on one screen only made people ask
                                        which one was right. Anyone Finish these
                                        genuinely cannot handle keeps a link out
                                        over there, where the reason is next to
                                        it. */}
                                    <td className="text-muted">{row.reason}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="text-muted small mb-3">
                            <strong>Finish these</strong> collects the CVs and
                            dates these need, without leaving this window. Names
                            are blank until the LinkedIn sync runs, so the link
                            is the way to tell them apart.
                          </div>
                        </>
                      )}

                      {result.record.not_found.length > 0 && (
                        <div className="text-muted small mb-3">
                          {result.record.not_found.length} row
                          {result.record.not_found.length === 1 ? "" : "s"} had
                          no status written — already removed from this role, or
                          not found on it.
                        </div>
                      )}
                    </>
                  )}

                  <div className="text-muted small">
                    {plan?.duplicates > 0 && (
                      <div className="mb-2">
                        {plan?.duplicates} duplicate link
                        {plan?.duplicates === 1 ? "" : "s"} removed before
                        sending.
                      </div>
                    )}
                    {skippedPanel()}
                    <div className="mt-2">
                      Profiles fill in on their own within about 20 minutes, as
                      long as this role is active.
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* The theme's modal-footer sets no gap, so the buttons sit flush
                against each other once there are more than two. */}
            <div className="modal-footer" style={{ gap: "10px" }}>
              {stage === "confirm" && (
                <>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => goBack("confirm")}
                  >
                    Back
                  </button>
                  {/* Download sits with the list of skipped rows above, not
                      here - the footer is for leaving the screen. */}
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={runImport}
                  >
                    Import {plan.urls.length} people
                  </button>
                </>
              )}

              {stage === "done" && (
                <>
                  {/* Both downloads sit with the lists they belong to, higher
                      up the screen. The footer is for leaving. */}
                  {/* Only when there is something to finish. An import that
                      placed everyone never sees this. */}
                  {/* Both exits are shut until the compensation panel has
                      finished - including while it is still finding each
                      person's employer, which is the slow part and the one
                      that actually caught someone out. Leaving unmounts the
                      panel, the lookup stops, the write never happens, and
                      every confirmed figure goes quietly. */}
                  {compRecording && (
                    <div
                      className="me-auto"
                      style={{ fontSize: "12.5px", color: ACCENT, fontWeight: 600 }}
                    >
                      {compRecording}
                    </div>
                  )}

                  {worklist().length > 0 && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={Boolean(compRecording)}
                      title={
                        compRecording ||
                        "Collect the CVs, dates and panels PINE needs for the people listed above."
                      }
                      onClick={() => setStage("finish")}
                    >
                      Finish these
                    </button>
                  )}
                  {!compRecording && (
                    <button
                      type="button"
                      className={
                        worklist().length > 0
                          ? "btn btn-outline-secondary"
                          : "btn btn-primary"
                      }
                      // "Done" reads like it finishes something, and with
                      // people still listed above it is fair to wonder what
                      // happens to them. It closes; everything above is
                      // already written.
                      title={
                        worklist().length > 0
                          ? `Finish this import. Everything above is already saved — ` +
                            `the ${worklist().length} people still listed keep the ` +
                            `status they have, and this import will not be offered ` +
                            `again.`
                          : "Finish this import. Everything above is already saved."
                      }
                      onClick={endImport}
                    >
                      Done
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /* Step 2 - choose sheets                                            */
  /* ---------------------------------------------------------------- */

  const linkedNow = current ? linkIndexes(current) : [];

  return (
    <div className="modal fade show d-block" tabIndex="-1">
      <div
        className="modal-dialog modal-dialog-centered"
        style={{ maxWidth: "1290px", width: "calc(100% - 48px)" }}
      >
        <div
          className="modal-content"
          style={{ height: "calc(100vh - 76px)", maxHeight: "790px" }}
        >
          {/* header */}
          <div
            className="flex-shrink-0 d-flex align-items-start gap-3"
            style={{ padding: "20px 26px 18px", borderBottom: "1px solid #eeeae5" }}
          >
            <div>
              <div style={{ fontSize: "19px", fontWeight: 700, color: "#1c1917" }}>
                Import Candidates from Excel
              </div>
              <div style={{ marginTop: "6px", fontSize: "13px", color: "#78716b" }}>
                {stepLabel("sheets")} · Choose one or more sheets to import
                from{" "}
                <span style={{ color: "#44403c", fontWeight: 600 }}>
                  {book.fileName}
                </span>
              </div>
            </div>

            {/* Removed here too - three hardcoded segments where the subtitle
                counts a different total. */}
            <div className="ms-auto d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn-close ms-2"
                onClick={() => onClose()}
              ></button>
            </div>
          </div>

          {/* body: preview on the left, sheet list on the right */}
          <div className="flex-grow-1 d-flex" style={{ minHeight: 0 }}>
            {/* ---------------- preview ---------------- */}
            <div
              className="d-flex flex-column"
              style={{ flex: 1, minWidth: 0, background: "#fbfaf9" }}
            >
              <div
                className="flex-shrink-0 d-flex align-items-center gap-3 flex-wrap"
                style={{
                  padding: "14px 22px",
                  borderBottom: "1px solid #eeeae5",
                  background: "#fff",
                }}
              >
                <div style={{ fontSize: "14px", fontWeight: 700, color: "#1c1917" }}>
                  {current.name}
                </div>
                <div style={{ fontSize: "12px", color: "#8a837c" }}>
                  {current.shape}
                </div>

                <div className="ms-auto d-flex align-items-center gap-2">
                  <div style={{ fontSize: "12px", color: "#78716b" }}>
                    Header row
                  </div>
                  <select
                    className="form-select form-select-sm"
                    style={{
                      // width:auto shrinks the box to the text, and the theme
                      // paints the chevron as a background image at a fixed
                      // offset from the right edge - so it lands on top of
                      // "Row 1". The padding reserves its lane.
                      width: "auto",
                      minWidth: "92px",
                      paddingRight: "30px",
                      fontSize: "12px",
                      fontWeight: 600,
                    }}
                    value={current.headerRow}
                    onChange={(e) => changeHeaderRow(Number(e.target.value))}
                  >
                    {Array.from({ length: HEADER_CHOICES }, (_, i) => {
                      const row = current.firstRow + i;
                      return (
                        <option key={row} value={row}>
                          Row {row + 1}
                        </option>
                      );
                    })}
                  </select>

                  {current.hiddenCount > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm"
                      style={{
                        border: "1px solid #e4e0da",
                        fontSize: "12px",
                        color: showHidden ? ACCENT : "#57534e",
                      }}
                      onClick={() => setShowHidden((v) => !v)}
                    >
                      <span
                        className="d-inline-block me-2"
                        style={{
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          background: showHidden ? ACCENT : "#a8a29b",
                        }}
                      />
                      {current.hiddenCount} hidden column
                      {current.hiddenCount === 1 ? "" : "s"}
                    </button>
                  )}
                </div>
              </div>

              {/* the grid */}
              {renderPreviewGrid(current, { link: linkedNow })}

              {/* tabs for the selected sheets */}
              <div
                className="flex-shrink-0 d-flex align-items-center gap-2"
                style={{
                  height: "40px",
                  borderTop: "1px solid #eeeae5",
                  background: "#fff",
                  padding: "0 16px",
                }}
              >
                <div
                  className="d-flex align-items-center gap-1"
                  style={{ minWidth: 0, overflow: "hidden" }}
                >
                  {/* Selected sheets, plus whatever is being previewed - so
                      the strip is not empty before anything is ticked. */}
                  {sheets.map((sheet, i) =>
                    selected.includes(sheet.name) || i === active ? (
                      <div
                        key={sheet.name}
                        role="button"
                        onClick={() => setActive(i)}
                        style={{
                          height: "26px",
                          padding: "0 12px",
                          borderRadius: "6px 6px 0 0",
                          background: i === active ? ACCENT_SOFT : "#faf9f7",
                          border: `1px solid ${
                            i === active ? ACCENT_LINE : "#eae6e1"
                          }`,
                          borderBottom: "none",
                          fontSize: "12px",
                          fontWeight: 700,
                          color: i === active ? ACCENT : "#78716b",
                          display: "flex",
                          alignItems: "center",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {sheet.name}
                      </div>
                    ) : null,
                  )}
                </div>
                <div
                  className="ms-auto"
                  style={{
                    fontSize: "11.5px",
                    color: "#a8a29b",
                    whiteSpace: "nowrap",
                  }}
                >
                  Preview only · nothing is saved yet
                </div>
              </div>
            </div>

            {/* ---------------- sheet list ---------------- */}
            <div
              className="d-flex flex-column"
              style={{
                width: "322px",
                flex: "none",
                borderLeft: "1px solid #eeeae5",
                background: "#fff",
              }}
            >
              <div
                className="flex-shrink-0 d-flex align-items-start gap-3"
                style={{ padding: "16px 18px 10px" }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{ fontSize: "13px", fontWeight: 700, color: "#1c1917" }}
                  >
                    Sheets in this file
                  </div>
                  <div
                    style={{ marginTop: "4px", fontSize: "12px", color: "#8a837c" }}
                  >
                    {sheets.length}{" "}
                    {sheets.length === 1
                      ? "sheet"
                      : "sheets · tick every sheet to import"}
                  </div>
                </div>
                <div
                  role="button"
                  onClick={toggleAll}
                  className="ms-auto"
                  style={{
                    flex: "none",
                    fontSize: "12px",
                    fontWeight: 700,
                    color: ACCENT,
                    paddingTop: "2px",
                  }}
                >
                  {allSelected ? "Clear all" : "Select all"}
                </div>
              </div>

              <div
                className="d-flex flex-column gap-2"
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                  padding: "0 12px 12px",
                }}
              >
                {sheets.map((sheet, i) => {
                  const on = selected.includes(sheet.name);
                  const chosenCols = linkIndexes(sheet);
                  return (
                    <div
                      key={sheet.name}
                      role="button"
                      onClick={() => {
                        toggleSheet(sheet.name);
                        setActive(i);
                      }}
                      style={{
                        border: `1.5px solid ${
                          i === active ? ACCENT : on ? ACCENT_LINE : "#eae6e1"
                        }`,
                        background: on ? ACCENT_SOFT : "#fff",
                        borderRadius: "10px",
                        padding: "12px 13px",
                        display: "flex",
                        gap: "11px",
                        alignItems: "flex-start",
                      }}
                    >
                      <div
                        style={{
                          width: "17px",
                          height: "17px",
                          flex: "none",
                          marginTop: "1px",
                          borderRadius: "4px",
                          border: `1.5px solid ${on ? ACCENT : "#d6d1ca"}`,
                          background: on ? ACCENT : "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontSize: "10px",
                          fontWeight: 700,
                        }}
                      >
                        {on ? "✓" : ""}
                      </div>

                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: 700,
                            color: "#1c1917",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={sheet.name}
                        >
                          {sheet.name}
                        </div>
                        <div
                          style={{
                            marginTop: "3px",
                            fontSize: "11.5px",
                            color: "#78716b",
                          }}
                        >
                          {sheet.shape}
                        </div>
                        <div
                          className="d-flex align-items-center gap-2 flex-wrap"
                          style={{ marginTop: "8px" }}
                        >
                          <div
                            style={{
                              fontSize: "11px",
                              fontWeight: 700,
                              padding: "3px 7px",
                              borderRadius: "5px",
                              ...(TAG_STYLE[sheet.level] || TAG_STYLE.muted),
                            }}
                          >
                            {sheet.tag}
                          </div>
                          <span
                            role="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActive(i);
                            }}
                            style={{
                              fontSize: "11px",
                              fontWeight: 700,
                              color: i === active ? ACCENT : "#a8a29b",
                            }}
                          >
                            {i === active ? "Previewing" : "Preview"}
                          </span>
                        </div>

                        {/* which columns the URLs come from */}
                        <div style={{ marginTop: "8px" }}>
                          {sheet.linkColumns.length === 0 ? (
                            <div
                              style={{ fontSize: "11px", color: "#b91c1c" }}
                            >
                              No LinkedIn URLs found
                            </div>
                          ) : (
                            <>
                              <div
                                style={{
                                  fontSize: "10.5px",
                                  color: "#8a837c",
                                  letterSpacing: "0.04em",
                                }}
                              >
                                LINKEDIN COLUMNS
                              </div>
                              <div className="d-flex flex-wrap gap-1 mt-1">
                                {sheet.linkColumns.map((col) => {
                                  const picked = chosenCols.includes(col.index);
                                  return (
                                    <span
                                      key={col.index}
                                      role="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleLinkColumn(sheet, col.index);
                                      }}
                                      title={col.label || "no header"}
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        padding: "2px 7px",
                                        borderRadius: "5px",
                                        border: `1px solid ${
                                          picked ? ACCENT : "#e4e0da"
                                        }`,
                                        color: picked ? ACCENT : "#a8a29b",
                                        background: "#fff",
                                      }}
                                    >
                                      {picked ? "✓ " : ""}
                                      {col.letter} · {col.sample}
                                      {col.sample >= 200 ? "+" : ""}
                                    </span>
                                  );
                                })}
                              </div>

                              {/* A handful of links across a long sheet is
                                  usually incidental - one consultant's note
                                  with a profile pasted into it, inside a list
                                  of companies. Said, not blocked: a genuine
                                  candidate buried in a target list is a real
                                  thing and refusing it would lose them. */}
                              {(() => {
                                const links = sheet.linkColumns.reduce(
                                  (sum, col) => sum + (col.sample || 0),
                                  0,
                                );
                                const rows = sheet.dataRowCount || 0;
                                if (rows < 10) return null;
                                if (links / rows >= 0.1) return null;

                                return (
                                  <div
                                    style={{
                                      marginTop: "5px",
                                      fontSize: "11px",
                                      color: "#8a5a1b",
                                      lineHeight: 1.45,
                                    }}
                                  >
                                    Only {links} link{links === 1 ? "" : "s"} in{" "}
                                    {rows} rows — this may not be a candidate
                                    list.
                                  </div>
                                );
                              })()}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div
                className="flex-shrink-0"
                style={{
                  borderTop: "1px solid #f0ece7",
                  padding: "14px 18px",
                  background: "#fbfaf9",
                }}
              >
                <div
                  style={{ fontSize: "12px", fontWeight: 700, color: "#44403c" }}
                >
                  Selected
                </div>
                <div
                  style={{
                    marginTop: "6px",
                    fontSize: "12.5px",
                    color: "#57534e",
                    lineHeight: 1.5,
                  }}
                >
                  {selectionSummary}
                </div>

                {sheetsWithoutLinks.length > 0 && (
                  <div
                    style={{
                      marginTop: "8px",
                      fontSize: "12px",
                      color: "#b91c1c",
                      lineHeight: 1.5,
                    }}
                  >
                    No LinkedIn links found in{" "}
                    {sheetsWithoutLinks.length === 1 ? "" : "these: "}
                    <strong>{sheetsWithoutLinks.join(", ")}</strong>.{" "}
                    {sheetsWithoutLinks.length === 1
                      ? "Nothing would be imported from it — unselect it to continue."
                      : "Nothing would be imported from them — unselect them to continue."}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* footer */}
          <div
            className="flex-shrink-0 d-flex align-items-center gap-3"
            style={{
              padding: "14px 22px",
              borderTop: "1px solid #eeeae5",
              background: "#fff",
            }}
          >
            <div style={{ fontSize: "12.5px", color: "#78716b" }}>
              {book.fileName} ·{" "}
              <span
                role="button"
                style={{ color: ACCENT, fontWeight: 600 }}
                onClick={() => {
                  setBook(null);
                  setFile(null);
                  setSelected([]);
                  setLinkChoice({});
                  setCompChoice({});
                }}
              >
                Change file
              </span>
            </div>

            <div className="ms-auto d-flex gap-2">
              {/* Back from here means the file screen. "Change file" above does
                  the same thing, but a Back button is where someone looks for
                  it - every other step has one. */}
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => goBack("sheets")}
              >
                Back
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => onClose()}
              >
                Cancel
              </button>
              {/* A sheet with no links costs a mapping call and contributes
                  nobody, so it is refused rather than carried through. The
                  reason is already on screen beside the selection. */}
              <StepButton
                // goToColumns is instant, but a file with no status and no
                // notes columns skips that screen entirely and goes straight
                // to the status check - which stays on THIS screen while it
                // asks PINE about everyone, 100 at a time.
                busy={confirmBusy}
                busyLabel="Checking PINE..."
                disabled={
                  selected.length === 0 || sheetsWithoutLinks.length > 0
                }
                onClick={goToColumns}
              >
                Next
              </StepButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Shared styles for the row-number gutter and the A/B/C letter strip. Both
   stay put while the grid scrolls, the way a spreadsheet does. */

const cellGutter = (height) => ({
  width: "44px",
  flex: "none",
  height: typeof height === "number" ? `${height}px` : height,
  background: "#f0ece7",
  borderRight: "1px solid #e0dbd4",
  borderBottom: "1px solid #eeeae5",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "11px",
  color: "#a8a29b",
  fontFamily: "ui-monospace,Menlo,monospace",
  position: "sticky",
  left: 0,
  zIndex: 2,
});

const cellLetter = () => ({
  // Grows to fill when a sheet has only a few columns, holds COL_WIDTH and
  // scrolls when it has many. A three-column file used to leave two thirds of
  // the panel empty. The letter strip, the headers and the cells all share this
  // rule, or they stop lining up.
  flex: `1 0 ${COL_WIDTH}px`,
  minWidth: `${COL_WIDTH}px`,
  height: "28px",
  background: "#f0ece7",
  borderRight: "1px solid #e0dbd4",
  borderBottom: "1px solid #e0dbd4",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "11px",
  fontWeight: 700,
  color: "#78716b",
  fontFamily: "ui-monospace,Menlo,monospace",
});

export default ImportFromExcel;
